import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import JotFormEmbed from "../components/JotFormEmbed";
import { EXHIBITORS } from "../data/bizConnectExhibitors";
import "./BizConnectShowcase.css";

/* ============================================================================
   Biz Connect Chicago — event landing page. Served at /bizconnectchicago.

   The event was renamed from "The Biz Connect Showcase 2026"; the file, the
   component and the `.bcs` style namespace keep the old name so the upstream
   diff below stays readable. Only user-visible copy changed.

   Ported from the standalone single-page site at
   https://fatimasoni.github.io/biz-connect-showcase/

   ---------------------------------------------------------------------------
   UPSTREAM SYNC BASELINE
     repo:   github.com/fatimasoni-jamaat/biz-connect-showcase  (branch: main)
     file:   exhibitors.html
     commit: 374d7f4  "Swap the duplicated days stat for hours of programme"
     date:   2026-10-17

   The file to track is exhibitors.html, NOT index.html. Upstream split in two
   at b6fdbb6: index.html became the attendee-only page published to
   bizconnectchicago.org, and exhibitors.html is the full version — the one
   this page descends from. exhibitors.html was byte-identical to index.html
   at the split, so history before b6fdbb6 lives on index.html.

   NOTE ON THE 2026-10-17 SYNC. Upstream's 17 October burst put almost all of
   its work on index.html and left exhibitors.html behind — of twelve commits
   only two touched it (the preferred hotel). That resync was taken against
   BOTH files on the committee's instruction ("sync everything"), so the
   speakers section, the at-a-glance stats, the audience reasons, the one-
   ticket messaging and the Around-Chicago reorder below all come from
   index.html. Upstream's exhibitors.html does not have them yet; until it
   catches up, a plain exhibitors.html diff will under-report what this page
   already carries. Diff index.html too.

   To resync, diff upstream exhibitors.html against that commit and apply only
   what changed here — do NOT re-port the whole file:

     git clone https://github.com/fatimasoni/biz-connect-showcase /tmp/bcs
     git -C /tmp/bcs diff 75cd628..main -- exhibitors.html

   Then bump the commit/date above. Upstream keeps a CHANGELOG.md describing
   each change in prose, which is usually faster to read than the diff.

   LOCAL DIVERGENCES — deliberate; a resync must NOT revert these:
     1. Contact address is tr@chicagojamaat.org (CONTACT_EMAIL below).
        Upstream still hardcodes the placeholder info@bizconnectchicago.com.
     2. Dark mode and its toggle are removed (see the CSS header).
     3. Upstream's <nav> and <footer> are an in-page section nav and a contact
        band here; the site supplies the real chrome.
     4. Anchor links use scrollIntoView, not location.hash.
     5. Upstream's "embedded mode" (the .embedded class, --parent-nav-h and the
        iframe/pinned-header detection script added in 21ed65c and 0580691) is
        deliberately NOT ported. That machinery exists so the standalone HTML
        can sit in an iframe inside a host portal; this page is a React route
        rendered directly between the site's Navbar and Footer, so there is no
        second nav to measure. Its skip link and <main> landmark are supplied
        by the app shell. Ignore those hunks on every future resync.
     6. Pricing, the two-form split and the "Exhibit with us" hero are local —
        upstream has none of them. Anything a resync touches in the passes,
        pricing or registration sections needs checking against these first.
     7. The Hijri dates stay. Upstream stripped all five from index.html on
        17 Oct but deliberately kept them in exhibitors.html; this page is the
        Jamaat-facing one, so it keeps them too.
     8. Section order is local. Upstream runs hero → who → programme →
        speakers → passes → venue → faq → chicago; this page keeps pricing and
        the two forms between passes and programme. Only the Around-Chicago
        move (now last, after the FAQ) was taken from upstream.
     9. Live registration figures. The hero's "Registered" tile and the "In the
        room so far" panel under Who you'll meet read /api/bizconnect-stats
        (api/bizconnect-stats), which aggregates the attendee JotForm. Upstream
        has neither. Both render nothing / fall back to the static tiles when
        the endpoint is unavailable, so a resync can't break the page by
        omitting them — but it must not delete them. Figures come from the live
        endpoint or, failing that, a build-time snapshot (see useAttendeeStats).
   ---------------------------------------------------------------------------

   Notable differences from the source, all deliberate:
   - Styles are namespaced under `.bcs` (see BizConnectShowcase.css) so the
     event's design system cannot leak into the Tailwind app.
   - Dark mode and its toggle were dropped; the page sits between the site's
     light Navbar and Footer.
   - The source's own <nav> and <footer> became an in-page section nav and an
     event contact band, since the site supplies the real chrome.
   - Anchor links call scrollIntoView instead of changing location.hash —
     App.js scrolls to top on every router location change, which would fight
     in-page navigation.
   ========================================================================== */

// Upstream hardcodes a placeholder (info@bizconnectchicago.com). This is the
// real committee address, matching Footer.jsx and config/resourceTemplates.js.
// See LOCAL DIVERGENCES above — do not let a resync revert this.
const CONTACT_EMAIL = "tr@chicagojamaat.org";

/* ---------- registration forms ----------
   This page (the Jamaat-facing one) takes BOTH audiences, so it carries two
   JotForms — one per audience — each in its own section.
   bizconnectchicago.org is attendee-only and must not surface the exhibitor
   form at all.

   Both ids are audience-specific and must not be swapped or reused: an
   attendee sent to the exhibitor form is recorded as wanting booth space, and
   vice versa. Setting an `id` to null is the supported way to take a form
   offline — RegisterSection then renders a "not open yet" panel pointing at
   the committee inbox, which is always better than showing the wrong form. */
const REGISTRATION = {
  attendee: {
    // JotForm title: "Attendee Form - The Biz Connect Showcase2026"
    id: "262430125585151",
    // Minutes quoted in the form-note; keep honest to the real form length.
    // TODO(committee): unverified for this form — 3 was the old combined
    // form's estimate. Correct it if the attendee form is longer or shorter.
    minutes: "3",
  },
  exhibitor: {
    // JotForm title: "Welcome to The Biz Connect Showcase2026"
    id: "261984324758066",
    minutes: "3",
  },
};

const jotformUrl = (id) => `https://form.jotform.com/${id}`;
const IMG_BASE = "/biz-connect/img";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------- icon helper ---------- */
const Ic = ({ name, lg }) => (
  <svg className={lg ? "ic ic-lg" : "ic"} aria-hidden="true">
    <use href={`#i-${name}`} />
  </svg>
);

/* ---------- content data ---------- */

/* The hero's at-a-glance figures, which replaced the countdown on 17 Oct — a
   ticking clock seven weeks out is motion without information.

   Every figure here has to be derivable from something else on this page, or
   it is a claim nobody can check: 20 is the length of SPEAKERS, 12 is the
   number of named non-keynote sessions in AGENDA. Both moved on 4 Nov when the
   committee's 3 Nov schedule was reconciled in: it filled every remaining open
   slot, so the sessions figure jumped from 8. Re-count both after any agenda
   change. The entry tile said "$5" until
   21 Oct, when the committee pulled every ticket price; it returned as "Free" on
   the 21st once the committee made entry free with pre-registration. "Free" is
   the one tile that is a commitment rather than a count — the attendee JotForm
   still states a fee and asks for Zelle payment, and until that is changed on
   JotForm the form contradicts this tile. Re-count after any agenda change — the
   sessions figure went stale at 6 the moment 3:00 pm stopped being an open slot
   on both days.

   The exhibitor count is the one exception and is flagged where it is set.

   "14 Hours / Of programme across the weekend" used to lead this row and came
   out when the exhibitor count went in: the meta block two lines above already
   says "8:30am – 3:30pm both days", so the hours were stated twice within a
   screen. */
const GLANCE = [
  /* Counts the imported array, not a number somebody retyped. The tile said 80
     while the committee's own sheet listed 53 confirmed — see EXHIBITOR_COUNT. */
  ["store", String(EXHIBITORS.length), "Exhibitors", "Signed so far, more to come"],
  ["users", "20", "Speakers", "Confirmed so far, more to come"],
  ["mic", "12", "Sessions", "Plus two keynotes"],
  ["ticket", "Free", "Entry", "Pre-registration required"],
];

/* The exhibitor line under the stats. An expo is judged on how many businesses
   are on the floor, so the figure leads the stat row and this says what kind of
   floor it is. Manufacturing is called out because it is the one pavilion with
   a session attached to it — see AGENDA, Saturday 2:00 pm.

   This was the hardcoded string "80" with a TODO asking the committee to keep
   it current. It never was: the sheet it came from lists 53 exhibitors who have
   either paid or confirmed, and the hero was promising 80. Now it counts the
   array, so the stat, the pavilion strip and the grid cannot disagree — and the
   number moves on its own the next time someone re-runs the import script. */
const EXHIBITOR_COUNT = String(EXHIBITORS.length);

/* Pavilion name → how many exhibitors are in it, biggest first.
 *
 * Both sites have advertised "eight industry pavilions" and "Eight focused
 * zones" since launch without ever naming one of them. These are those eight,
 * and they are the sheet's own industry values — so if a ninth ever appears the
 * import script throws rather than quietly making the page a liar. */
const PAVILION_COUNTS = (() => {
  const counts = new Map();
  for (const { pavilion } of EXHIBITORS) counts.set(pavilion, (counts.get(pavilion) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "en"));
})();

/* A monogram for a company is not initials for a person. `initials()` takes the
   first and last word, which is right for "Abidali Neemuchwala" and wrong for
   "The Premium Basket" (TB) or "53 Jewels" (5J). Leading articles go, digits
   are kept only when the name starts with one, and a single-word name gives its
   first two letters so the disc never holds a lone character. */
const companyMark = (name) => {
  const words = name
    .replace(/[^\p{L}\p{N}\s&]/gu, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w, i) => !(i === 0 && /^(the|a|an)$/i.test(w)) && w !== "&");
  if (!words.length) return "??";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/* Logo filenames are not derived here the way speakerImg derives portraits.
   Logos arrive in whatever format the company publishes — SVG, PNG and JPG are
   all in use — and the card needs to know whether a logo exists at all before
   it renders, to pick the slot shape. So the import script resolves both
   against disk and writes the filename into each record's `logo`. */

/* Who you'll meet. Six roles, each with the reason that role is in the room —
   taken from the committee's "Biz Connect Showcase — Event of the Year 2026"
   brochure, page 7 ("Who you'll meet · Every seat has a purpose").

   This replaced twelve generic chips (Entrepreneurs, Business owners,
   Professionals, Manufacturers, …) that named audiences without saying why any
   of them was there. A motive is what makes a room legible: it tells a reader
   who they would want to find, and whether anyone would want to find them. */
const AUDIENCE = [
  ["briefcase", "Executives", "Seeking partners and vendors."],
  ["chart", "Business owners", "Scaling or succession planning."],
  ["rocket", "Founders", "Ready to raise or launch."],
  ["star", "Investors", "Looking for vetted opportunities."],
  ["users", "Community leaders", "Builders of local commerce."],
  ["user", "Professionals", "Advisors, consultants, specialists."],
];

/* The eight reasons from the same brochure, page 3 ("What's in it for you").
   These replaced six bullets that promised outcomes in the abstract — "build
   relationships that outlast the weekend" — where these name the thing that
   produces the outcome.

   NOTE on the seventh: the brochure titles it "Manufacturing" but its body
   reads "Learn from CEOs as they share their journeys and insights", which
   describes CEO Stories, the Saturday keynote. The body is unambiguous and
   matches a real session, so it is titled CEO Stories here.
   TODO(committee): confirm — if a manufacturing reason was intended, the
   Manufacturing Opportunities session would be the one to point at. */
const WHY_ATTEND = [
  ["bulb", "Fresh perspectives", "Hear new ideas and trends from across industries."],
  [
    "store",
    "Sector pavilions",
    "Eight focused zones — spend your time where your industry lives.",
  ],
  [
    "chart",
    "Brand visibility",
    "Show your products and services to people who buy, partner and invest.",
  ],
  ["panel", "Real conversations", "Step away from the screen and talk face to face."],
  [
    "wrench",
    "Hands-on workshops",
    "Government contracting, sales strategy, and more.",
  ],
  ["users", "Connections that last", "Build a network you can tap long after this weekend."],
  ["mic", "CEO stories", "Learn from CEOs as they share their journeys and insights."],
  [
    "ticket",
    "Networking bingo",
    "Turn introductions into connections with a playful challenge.",
  ],
];

const WHY_EXHIBIT = [
  "Put your product in front of decision-makers who buy, distribute and refer.",
  "Generate qualified leads across two days of concentrated footfall.",
  "Launch or demonstrate something new to an audience primed to look.",
  "Meet distributors, retailers and manufacturers you would otherwise cold-email.",
  "Build brand recognition across the North American Bohra business community.",
  "Sit alongside businesses at every stage — and learn from the floor as much as you sell on it.",
];

/* ---------- pricing ----------
   Committee figures, transcribed from the exhibitor-pricing and sponsorship
   sheets. Treat these as the source of truth for the #pricing tables AND for
   the "from" figures on the pass cards in #passes — if a rate changes here,
   the pass card must change with it.

   The detail strings are kept verbatim from the sheet, including "Premium
   Sponsor Package": there is no Premium tier in SPONSORSHIP below (the tiers
   are Platinum/Gold/Silver/Bronze/Community), and "Premium" is the Gold
   package's marketing level. Flagged with the committee rather than silently
   relabelled — guessing wrong on a price sheet is worse than quoting it. */
const BOOTHS = [
  { type: "Wall booth (8×10)", detail: "Wall booth", price: "$350" },
  { type: "Standard booth (8×10) exhibit", detail: "Standard booth", price: "$550" },
  {
    type: "Premium booth exhibit",
    detail: "12×12 (included with the Premium Sponsor Package)",
    price: "$750",
  },
  {
    type: "Platinum booth exhibit",
    detail: "16×12 (included with the Platinum Sponsor Package)",
    price: "$950",
  },
  {
    type: "Food vendor booth",
    detail: "2 days option / 1 day option",
    price: "$221 / $153",
  },
];

const SPONSOR_TIERS = ["Platinum", "Gold", "Silver", "Bronze", "Community"];

/* One row per benefit, values positional against SPONSOR_TIERS.
   `true` renders a tick, `null` renders an em dash — "not included" has to read
   as a deliberate blank rather than a gap in the data. */
const SPONSORSHIP = [
  { benefit: "Pricing", values: ["$4,500", "$3,100", "$1,200", "$975", "$253"], head: true },
  { benefit: "Speaking opportunity", values: ["3–5 minutes", null, null, null, null] },
  {
    benefit: "Stage recognition",
    values: [
      "Individual shout-out",
      "Individual shout-out",
      "Group mention",
      "Group mention",
      "Group thank-you",
    ],
  },
  { benefit: "Marketing level", values: ["Platinum", "Premium", "Standard", "Standard", null] },
  { benefit: "Exhibitor booth", values: ["Platinum booth", "Premium booth", null, null, null] },
  { benefit: "Video advertisement", values: ["3–4 minutes", "1–2 minutes", null, null, null] },
  { benefit: "Event logo display", values: [true, true, true, true, null] },
  {
    benefit: "Program listing",
    values: ["Full feature", "Large", "Medium", "Standard", "Name only"],
  },
  { benefit: "Digital promotion & attendee access", values: [true, true, true, true, null] },
  { benefit: "Complimentary invitations", values: ["6", "3", "2", "2", null] },
];

/* Two days, 5–6 December. Friday 4 December ("Orientation") was removed from
   the programme entirely rather than folded into another day, so the Opening
   Ceremony & Keynote, the Business Reception Darees, the Exhibitors & Industry
   Leaders Intro and the Friday dinner are all off the schedule — not moved.
   Anything that promised them elsewhere on the page had to go with them (the
   Darees pass inclusion, the day-one intro in the booth FAQ answer). */
const DAYS = [
  {
    no: "01",
    title: "Discover",
    theme: "Explore",
    date: "Saturday 5 December · 26 Jamadul Ukhra · 8:30am – 3:30pm",
    focus: "Learn • Explore • Collaborate",
    items: [
      ["store", "Business Expo & Product Showcase"],
      ["panel", "Expert Speakers & Industry Panels"],
      ["factory", "Manufacturing Pavilion"],
      ["play", "Live Demonstrations"],
    ],
  },
  {
    no: "02",
    title: "Grow",
    theme: "Thrive",
    date: "Sunday 6 December · 27 Jamadul Ukhra · 8:30am – 3:30pm",
    focus: "Lead • Innovate • Thrive",
    items: [
      ["rocket", "Startup & Innovation Spotlight"],
      ["chart", "Leadership & Business Growth Sessions"],
      ["mic", "Closing Keynote"],
      ["award", "Recognition Ceremony & Final Networking"],
    ],
  },
];

/* ---------- speakers & sessions ----------
   Source of truth is the committee's own schedule document, "BizConnect
   Showcase Chicago 2026 Speaker Schedule", footed "Draft schedule as of
   October 15, 2026":
   docs.google.com/document/d/1lyRZ6yQZTmLn0d_8ID1BmneQRtwzM3bx

   Mirror it slot for slot, INCLUDING the open ones. The doc runs 9am-3pm on
   Saturday and 10am-3pm on Sunday and marks five of those hours "Open slot —
   Program to be announced"; those render as `open: true` cards. Dropping them
   silently would make a half-built programme look like a finished one, and
   would leave the page claiming 8:30am-3:30pm days with nothing in the
   morning.

   Two rules the source imposes and this page follows:

   1. A KEYNOTE IS NAMED ONLY ONCE THE COMMITTEE CONFIRMS ACCEPTANCE. The doc
      is a draft and its keynote rows are written as candidates, so the name
      beside a slot is not by itself permission to publish it.
      - Saturday 11am is ONE session: the keynote, titled "CEO Stories",
        given by Abidali Neemuchwala. Confirmed by Mansoor on 2026-10-16 and
        published on that confirmation. The doc's cell stacks the slot label,
        the session title and the speaker, which is easy to read as two or
        three separate rows — it is not.
      - Sunday 11am read "Keynote Open" — a keynote slot with no session and
        no speaker — until 31 Oct, when the committee confirmed Abeezar
        Tybejee. It is a CEO Stories keynote like Saturday's. That is the
        committee's framing: two days, two CEOs, one session title.
   2. Kristin Richards is NOT on this page. She had been published as Director
      of the Illinois Dept. of Commerce & Economic Opportunity on the
      Manufacturing panel; the updated agenda replaces her with "Illinois
      Manufacturing Association (TBD)". Do not restore her from an older draft.

   Also deliberately unpublished: the TR/Expo Coordinator assignments and the
   MC list, which are internal organising data.

   Photographs are optional. `speakerImg` slugs the name, so dropping
   public/biz-connect/img/speaker-saba-haider.jpg in is the whole change —
   until then the avatar shows initials on a violet disc, which reads as
   deliberate rather than broken. */
const SPEAKERS = [
  {
    name: "Abidali Neemuchwala",
    session: "CEO Stories",
    /* TODO(committee): the 3 Nov schedule reverts this to "Co-Founder and
       Director at Dallas Venture Capital" and adds a Wipro bio. Both are held
       back pending a decision — this title has been live since 23 Oct. */
    title: "Tech Investor and Ex-CEO Wipro",
    keynote: true,
  },
  { name: "Saba Haider", session: "Community & Public Leadership" },
  { name: "Alifya Kagalwala", session: "AI for SMBs" },
  { name: "Mufaddal Khambaty", session: "AI for SMBs" },
  { name: "Abdeali Nagarwala", session: "AI for SMBs" },
  // Had no slot from 27 Oct until the 3 Nov schedule put him on the AI panel.
  { name: "Yusuf Hakim", session: "AI for SMBs" },
  {
    name: "Ian Quan",
    session: "Working with the Government",
    title: "APEX Accelerator",
    bio: "Ian has over 20 years of government contracting and sales experience, and has helped his companies win over $100 million in contracts at federal and state level — including nationwide DHS, Navy, Army Corps, Air Force and TSA multi-year, fixed, IDIQ and BPA contracts.",
  },
  { name: "Hussain Ezzy", session: "Working with the Government" },
  { name: "Fakhruddin Cutlerywala", session: "Working with the Government" },
  { name: "Huzaifa Hussain", session: "Working with the Government" },
  // Added by the 3 Nov schedule.
  { name: "Shabbir Katabji", session: "Working with the Government" },
  {
    name: "Willette LeGrant",
    session: "Manufacturing Opportunities",
    title: "U.S. Small Business Administration",
  },
  // Added by the 3 Nov schedule, with the Sunday 12:00 slot it created.
  {
    name: "Turan Quettawala",
    session: "Insights from Manufacturers",
    title: "Director, Jafferjees",
    bio: "A Director at Jafferjees, the Pakistani retail leather goods brand, where he is the fifth generation running the family business and oversees financial reporting and marketing strategy. Before returning to Karachi in 2019 he spent more than 20 years in global equity research — as an analyst at UBS Investment Bank and JP Morgan, then seventeen years at Scotiabank Global Banking and Markets in Toronto, where as Director, Equity Research he led coverage of the aerospace and transportation sectors. He is a CFA Charterholder with an MBA in Finance from the Institute of Business Administration, Karachi.",
  },
  {
    name: "Tasneem Talib Ali",
    session: "Sell Differently",
    title: "Founder, Fervent Living",
    bio: "A leadership coach and trainer focused on building confident, resilient and growth-oriented people, through leadership and mindset training, one-to-one coaching and group programmes. She also holds a senior leadership position at Kantar, bringing over 20 years of experience across multiple markets to the blend of business acumen and practical coaching she teaches.",
  },
  { name: "Tasneem Khokha", session: "Biz Lifecycle" },
  // "Mustaali Kanch" until the 3 Nov schedule spelled it out in full.
  { name: "Mustaali Kanchwala", session: "Biz Lifecycle" },
  { name: "Mustansir Saifuddin", session: "Biz Lifecycle" },
  { name: "Husaina Badshah", session: "Biz Lifecycle" },
  // Added by the 3 Nov schedule.
  { name: "Munira Zahabi", session: "Biz Lifecycle" },
  /* Sunday's keynote. Last in the array so .spk-card.key closes the grid the
     way Abidali's opens it. */
  {
    name: "Abeezar Tybejee",
    session: "CEO Stories",
    title: "CEO, Shipcom Wireless",
    keynote: true,
    bio: "With over 23 years of experience in startups, the Department of Defense and business development, Abeezar has built Shipcom into a trusted partner for agencies including the US Marines, US Navy, DHS, FEMA and more. Their flagship Shipcom AI platform powers the $14 billion supply chain of the US Navy’s DDG 51 Class Aegis Program, ensures IoT logistics and cybersecurity for DHS-CBP operations on the US Southern Border, and supports FEMA’s emergency supply chain needs.",
  },
];

const initials = (name) => {
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Only the keynote carries a title so far — one under every name in a
// four-person panel is clutter.
const speakerTitle = (name) => SPEAKERS.find((s) => s.name === name)?.title;

/* Three tints from the same hue family, picked deterministically from the
   name. Fourteen identical discs read as a placeholder grid rather than
   fourteen people, and two pairs collide on their initials — Abidali
   Neemuchwala and Abdeali Nagarwala are both "AN", Mufaddal Khambaty and
   Mustaali Kanch are both "MK" — so without this they are literally
   indistinguishable until someone supplies photographs.

   All three carry white text above 5:1, checked: #6B2F9E 8.1:1, #4A2470
   12.4:1, #8B4BBF 5.2:1. Keep any replacement above 4.5:1 — the initials are
   the only content in the disc. */
const AVATAR_TINTS = ["#6b2f9e", "#4a2470", "#8b4bbf"];

/* Assigned once, in list order, rather than hashed from the name: a hash can
   land two people on the same tint, and the pair it matters for is exactly the
   pair that already shares initials. Rotating by index gives the grid a steady
   rhythm, and the inner loop guarantees nobody sharing initials shares a tint
   — three tints covers it while no more than three people collide. */
const AVATAR_TINT_BY_NAME = (() => {
  const takenPerInitials = new Map();
  const assigned = new Map();
  SPEAKERS.forEach(({ name }, i) => {
    const key = initials(name);
    const taken = takenPerInitials.get(key) || new Set();
    let idx = i % AVATAR_TINTS.length;
    while (taken.has(idx)) idx = (idx + 1) % AVATAR_TINTS.length;
    taken.add(idx);
    takenPerInitials.set(key, taken);
    assigned.set(name, AVATAR_TINTS[idx]);
  });
  return assigned;
})();

const tintFor = (name) =>
  AVATAR_TINT_BY_NAME.get(name) ||
  AVATAR_TINTS[[...name].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR_TINTS.length];

const speakerImg = (name) =>
  `${IMG_BASE}/speaker-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.jpg`;

/* The rail. Both days run the identical seven hours, which is the whole reason
   the schedule can share one time axis. If a day ever gains an hour the other
   does not have, add it here — the empty side renders as a blank cell rather
   than knocking every row below it out of alignment. */
const AGENDA_HOURS = [
  "9:00 am",
  "10:00 am",
  "11:00 am",
  "12:00 pm",
  "1:00 pm",
  "2:00 pm",
  "3:00 pm",
];

const AGENDA = [
  {
    day: "Saturday 5 December",
    short: "Saturday",
    meta: "26 Jamadul Ukhra · 8:30am – 3:30pm",
    sessions: [
      { time: "9:00 am", title: "Networking and Welcome" },
      {
        time: "10:00 am",
        title: "Founder Stories Roundtable",
        note: "Four business owners from Chicago share their entrepreneurial journey — the highs and the lows.",
        tba: "Speakers to be announced.",
      },
      {
        time: "11:00 am",
        keynote: true,
        title: "CEO Stories",
        note: "Prominent CEOs share their journeys and what they learned along the way.",
        speakers: ["Abidali Neemuchwala"],
      },
      {
        time: "12:00 pm",
        title: "Community and Public Leadership",
        note: "A conversation on community priorities, civic engagement and the connections between business and public service.",
        speakers: ["Saba Haider"],
      },
      {
        time: "1:00 pm",
        title: "Working with the Government: Practical Workshop",
        note: "Understand procurement opportunities, tips, guidelines, tender readiness and the steps toward competing for government contracts with Apex Accelerator (Ian Quan), Hussain Ezzy, Fakhruddin Cutlerywala and Huzaifa Hussain.",
        speakers: [
          "Ian Quan",
          "Hussain Ezzy",
          "Fakhruddin Cutlerywala",
          "Huzaifa Hussain",
          "Shabbir Katabji",
        ],
      },
      {
        time: "2:00 pm",
        title: "Manufacturing Opportunities",
        note: "How to get into manufacturing — tips, grants, tax breaks and more, with the Illinois Manufacturing Association. From 2:00 to 2:30, Willette LeGrant of the U.S. Small Business Administration covers SBA manufacturing resources, the Made in America Initiative, and the recent DoW–SBA memorandum of understanding to strengthen the industrial defense base.",
        // She is the first named speaker on this slot; the Illinois
        // Manufacturing Association side of it still has nobody attached, so
        // the line narrows from "Speakers" to "Further speakers" rather than
        // disappearing. Drop it once the IMA names arrive.
        tba: "Further speakers to be announced.",
        speakers: ["Willette LeGrant"],
      },
      {
        time: "3:00 pm",
        title: "Raffles and Networking Bingo",
        note: "Prize raffles and the networking bingo challenge to close the day.",
      },
    ],
  },
  {
    day: "Sunday 6 December",
    short: "Sunday",
    meta: "27 Jamadul Ukhra · 8:30am – 3:30pm",
    sessions: [
      { time: "9:00 am", title: "Networking and Welcome" },
      {
        time: "10:00 am",
        title: "How AI can help SMBs grow revenue and scale their business",
        note: "A panel on using AI to generate more revenue for your business.",
        speakers: [
          "Alifya Kagalwala",
          "Mufaddal Khambaty",
          "Abdeali Nagarwala",
          "Yusuf Hakim",
        ],
      },
      {
        // Was "Keynote Open" — an open slot with no session and no speaker —
        // from the committee's draft until 31 Oct, when Abeezar Tybejee was
        // confirmed. Both days now run a CEO Stories keynote at 11:00; that is
        // the committee's own framing, not a duplicated row.
        time: "11:00 am",
        keynote: true,
        title: "CEO Stories",
        note: "Prominent CEOs share their journeys and learnings — so you don’t make the same mistake.",
        speakers: ["Abeezar Tybejee"],
      },
      {
        /* Created by the 3 Nov schedule. Turan Quettawala has the first half
           hour; the second is marked "Locked for Org (TBD)" in the committee's
           document, which is internal scheduling language and is not published
           as such — the to-be-announced line covers it. */
        time: "12:00 pm",
        title: "Insights from Manufacturers",
        note: "From 12:00 to 12:30, Turan Quettawala of Jafferjees on running a fifth-generation family manufacturer, and what two decades in global equity research taught him about it.",
        tba: "Further programme to be announced.",
        speakers: ["Turan Quettawala"],
      },
      {
        time: "1:00 pm",
        title: "Sell Differently: the mindset shift that changes everything",
        note: "What if your biggest sales barrier isn’t your strategy, your skills or your pipeline — but your mindset? How a shift in perspective can transform the way you sell, influence and win.",
        speakers: ["Tasneem Talib Ali"],
      },
      {
        time: "2:00 pm",
        title: "Biz Lifecycle: from start to finish",
        note: "Lessons on ideation, starting, sustaining, growing and scaling a business, including women’s entrepreneurship.",
        speakers: [
          "Tasneem Khokha",
          "Mustaali Kanchwala",
          "Mustansir Saifuddin",
          "Husaina Badshah",
          "Munira Zahabi",
        ],
      },
      {
        time: "3:00 pm",
        title: "Raffles and Networking Bingo",
        note: "Prize raffles and the networking bingo challenge to close the day.",
      },
    ],
  },
];

/* Guests of honour. From the committee's 2026 brochure, page 2
   ("Distinguished Guests of Honor"), which gives the two Congressmen a card
   each with their official portrait; the other three were only ever a line of
   names on this page.

   PHOTOGRAPH RIGHTS, for once, are not a question: both are official US House
   portraits, works of the federal government and therefore public domain. They
   came out of the brochure PDF and had its gold frame cropped off. The other
   three were supplied by the committee on 20 Oct; a card with no `img` falls
   back to initials exactly as the speaker cards do.

   This replaced a single "Also expected to attend:" notice that ran all five
   names together in one sentence at the foot of the agenda.
   TODO(committee): each office should be asked before their name and face sit
   on a public page — still outstanding, and more pointed now there are photos. */
const GUESTS = [
  {
    name: "Raja Krishnamoorthi",
    honorific: "The Honorable",
    role: "U.S. Representative, Illinois’ 8th District",
    img: "guest-raja-krishnamoorthi.jpg",
  },
  {
    name: "Jonathan L. Jackson",
    honorific: "The Honorable",
    role: "U.S. Representative, Illinois’ 1st District",
    img: "guest-jonathan-jackson.jpg",
  },
  // "Dr." is not a given name, so the derived initials would read "DP".
  {
    name: "Dr. M. Anand Prakash",
    role: "Consul General of India",
    initials: "AP",
    img: "guest-anand-prakash.jpg",
  },
  { name: "Zaman Mehdi", role: "Consul General of Pakistan", img: "guest-zaman-mehdi.jpg" },
  { name: "John Curran", role: "Illinois State Senator, 41st District", img: "guest-john-curran.jpg" },
];

const VENUE_FACTS = [
  ["pin", "Address", "625 Joliet Road, Willowbrook, Illinois 60527"],
  [
    "plane",
    "Getting there",
    "Roughly 25–30 minutes from Chicago Midway (MDW) and about 40 minutes from O’Hare (ORD), traffic permitting.",
  ],
  ["car", "Parking", "On-site parking available for attendees and exhibitors."],
  /* The "Hotels — details will be shared closer to the event" row moved out of
     this list and became HOTEL below, now that there is a hotel to name. */
];

/* The preferred hotel. The photograph is the hotel's own marketing image,
   supplied by Fatima — TODO(committee): permission to use it has not been
   confirmed with the hotel. Missing or pulled, the <figure> hides itself and
   the card degrades to text, same as Shot.
   TODO(committee): upstream states a discounted group rate but no booking
   code; if one was negotiated it belongs in this copy. */
const HOTEL = {
  img: "hotel-delta-willowbrook.jpg",
  alt: "Delta Hotels by Marriott Chicago Willowbrook, exterior entrance at dusk",
  name: "Delta Hotels by Marriott Chicago Willowbrook",
  body: "7800 S Kingery Highway, Willowbrook — a few minutes from the arena on Route 83. A discounted group rate is available for Biz Connect guests; mention Biz Connect Chicago when booking.",
  mapsQuery:
    "Delta Hotels by Marriott Chicago Willowbrook 7800 S Kingery Hwy Willowbrook IL 60527",
  site: "https://www.marriott.com/en-us/hotels/chido-delta-hotels-chicago-willowbrook/overview/",
};

const ATTRACTIONS = [
  {
    img: "chicago-artinstitute.jpg",
    alt: "Art Institute of Chicago",
    icon: "building",
    kind: "Museum",
    title: "Art Institute of Chicago",
    body: "One of the great encyclopaedic museums — the Impressionist rooms, American Gothic, and the Thorne Miniature Rooms. Book a timed entry slot.",
    href: "https://www.artic.edu/visit",
    cta: "Plan a visit",
  },
  {
    img: "chicago-skydeck.jpg",
    alt: "Skydeck at Willis Tower",
    icon: "chart",
    kind: "View",
    title: "Skydeck at Willis Tower",
    body: "103 floors up, with the glass Ledge boxes that cantilever out over Wacker Drive. Timed tickets, cheapest well before sunset.",
    href: "https://theskydeck.com/",
    cta: "Book tickets",
  },
  {
    img: "chicago-rivercruise.jpg",
    alt: "Architecture River Cruise",
    icon: "panel",
    kind: "Tour",
    title: "Architecture River Cruise",
    body: "The Chicago Architecture Center's docent-led cruise along all three branches of the river. Widely rated the single best thing to do in the city.",
    href: "https://www.architecture.org/tours/",
    cta: "Browse tours",
  },
  {
    img: "chicago-millennium.jpg",
    alt: "Millennium Park & The Bean",
    icon: "star",
    kind: "Free",
    title: "Millennium Park & The Bean",
    body: "Cloud Gate, the Pritzker Pavilion and Lurie Garden, all walkable from the Loop. No ticket needed.",
    href: "https://www.chicago.gov/city/en/depts/dca/supp_info/millennium_park.html",
    cta: "Park information",
  },
  {
    img: "chicago-msi.jpg",
    alt: "Museum of Science and Industry",
    icon: "bulb",
    kind: "Museum",
    title: "Museum of Science and Industry",
    body: "A captured U-505 submarine, a full coal mine and a 3,500 sq ft model railroad. The strongest pick if families are travelling with you.",
    href: "https://www.msichicago.org/visit/",
    cta: "Tickets & hours",
  },
  {
    img: "chicago-navypier.jpg",
    alt: "Navy Pier",
    icon: "play",
    kind: "Family",
    title: "Navy Pier",
    body: "The Centennial Wheel, boat departures and the lakefront promenade. Entry is free; rides and attractions are ticketed individually.",
    href: "https://navypier.org/",
    cta: "What's on",
  },
];

const mapsUrl = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

// Sit-down halal restaurants within ~5–22 minutes of Compass Arena.
const DINING_NEAR_VENUE = [
  {
    kind: "Levantine",
    title: "Damascus Falafel",
    meta: "approx. 5 min · 10S410 Kingery Hwy, Willowbrook",
    body: "The closest strong option to the arena, right up Route 83. Sit-down Levantine grills and mezze, 4.6 on Zabihah with a high halal rating. Prayer space on request.",
    query: "Damascus Falafel 10S410 Kingery Hwy, Willowbrook IL",
  },
  {
    kind: "Middle Eastern",
    title: "Kabob Q",
    meta: "approx. 7 min · 858 75th St, Willowbrook",
    body: "Full-service kebab house a couple of minutes further on 75th. Straightforward, comfortable, and easy for a group after sessions.",
    query: "Kabob Q 858 75th St, Willowbrook IL",
  },
  {
    kind: "Mediterranean",
    title: "Pita Pita Mediterranean Grill",
    meta: "approx. 20 min · 376 Route 59, Naperville",
    body: "Sit-down Mediterranean carrying Zabihah's top halal rating and a five-star average. The pick if you want somewhere properly nice for a business dinner.",
    query: "Pita Pita Mediterranean Grill 376 Route 59, Naperville IL",
  },
  {
    kind: "Afghan",
    title: "Afghani Box",
    meta: "approx. 18 min · 1220 W Ogden Ave, Naperville",
    body: "Full-service Afghan kitchen — kabuli pulao, mantu, charcoal kebabs. Also top halal rating and five stars.",
    query: "Afghani Box 1220 W Ogden Ave, Naperville IL",
  },
  {
    kind: "Middle Eastern",
    title: "Pitaville",
    meta: "approx. 22 min · 1552 N Aurora Rd, Naperville",
    body: "Larger sit-down room that handles bigger tables well. High halal rating across two dozen reviews.",
    query: "Pitaville 1552 N Aurora Rd, Naperville IL",
  },
  {
    kind: "Indian · Pakistani",
    title: "JK Kabab",
    meta: "approx. 20 min · 572 Weston Ridge Dr, Naperville",
    body: "The closest thing to familiar desi cooking done as a proper sit-down restaurant rather than a counter.",
    query: "JK Kabab 572 Weston Ridge Dr, Naperville IL",
  },
];

// Longer-standing city options, 25–40 minutes away.
const DINING_CITY = [
  {
    kind: "Indian · Pakistani",
    title: "Ghareeb Nawaz",
    body: "A Chicago institution — enormous plates, very cheap, no frills at all. Go for the food and the story, not the room.",
    href: "https://www.zabihah.com/halal-restaurants/chicago-il-us",
  },
  {
    kind: "Middle Eastern",
    title: "Naz's Halal Food",
    body: "Platters, gyros and grills. Also carries Zabihah's highest halal rating.",
    href: "https://www.zabihah.com/halal-restaurants/chicago-il-us",
  },
  {
    kind: "Mediterranean",
    title: "I Dream Of Falafel",
    body: "Fast-casual bowls and wraps with several Loop locations — the easiest option between sessions downtown.",
    href: "https://idreamoffalafel.com/",
  },
  {
    kind: "Indian · Pakistani",
    title: "Tandoor Char House",
    body: "Sit-down North Indian on Halsted, one of the higher-rated halal kitchens in the city.",
    href: "https://www.tandoorcharhouse.com/",
  },
];

const FAQS = [
  {
    q: "Who is this event for?",
    a: (
      <>
        Anyone doing business or preparing to. Established owners, professionals, manufacturers and
        retailers will find buyers and partners; startups, students and first-time founders will find
        mentors and a first audience. You do not need a booth to get value from both days.
      </>
    ),
  },
  {
    q: "How do I register?",
    a: (
      <>
        There are two forms — one for attendees and one for exhibitors. Pick whichever matches how you
        are joining; if you want a booth, use the exhibitor form (it covers attendee access for your
        booth team too). Have your industry and business name to hand. Booth and sponsorship rates are
        published in the pricing section; the committee will follow up with payment details for your
        pass.
      </>
    ),
  },
  {
    q: "Can I attend for only one day?",
    a: (
      <>
        Yes. One ticket covers both days and you are welcome at either. Saturday leans toward the
        exhibition floor, government procurement and manufacturing; Sunday toward AI, sales and the
        business lifecycle panel. The full agenda is in the speakers section above.
      </>
    ),
  },
  {
    q: "What does an exhibitor booth include?",
    a: (
      <>
        Booth space in the Business Expo &amp; Product Showcase, a directory listing, and attendee access
        for your booth team. Booth types run from a $350 wall booth to a $950 platinum booth, with a food vendor option —
        see the pricing section for the full table. Submit the <em>exhibitor</em> form to register your
        interest — the committee then follows up to confirm booth space and payment.
      </>
    ),
  },
  {
    q: "Is food available at the venue?",
    a: (
      <>
        Yes. Food stalls and vendors will be on site across both days serving halal food. Compass Arena
        is a combined arena and dining venue. There are also halal restaurants a few minutes away if you
        would rather go out — several are listed in the Chicago section below.
      </>
    ),
  },
  {
    q: "I want to sponsor or speak. Who do I contact?",
    a: (
      <>
        Reach out through the contact details below and the organising committee will send the sponsorship
        pack and speaking guidelines.
      </>
    ),
  },
];

/* "Passes" is deliberately absent: the "Register Now" button beside these
   links already goes to #passes, and having both was the same
   two-controls-one-destination problem as the old attendee/register overlap.

   Order follows the page, so Chicago is last — it moved below the FAQ on
   17 Oct, the tourism and city-restaurant content being a lot of room for a
   mostly local audience. */
const NAV_SECTIONS = [
  ["about", "About"],
  ["who", "Who Attends"],
  ["exhibitors", "Exhibitors"],
  ["pricing", "Pricing"],
  ["programme", "Programme"],
  ["speakers", "Speakers"],
  ["guests", "Guests"],
  ["venue", "Venue"],
  ["faq", "FAQ"],
  ["chicago", "Chicago"],
];

/* ---------- icon sprite ---------- */
const Sprite = () => (
  <svg className="sprite" aria-hidden="true" focusable="false" width="0" height="0">
    <defs>
      <symbol id="i-calendar" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></symbol>
      <symbol id="i-pin" viewBox="0 0 24 24"><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.6" /></symbol>
      <symbol id="i-building" viewBox="0 0 24 24"><path d="M4 21V4h10v17M14 9.5h6V21M7.5 8h3M7.5 12h3M7.5 16h3M16.5 13h1.5M16.5 17h1.5" /></symbol>
      <symbol id="i-users" viewBox="0 0 24 24"><circle cx="9" cy="8" r="3.2" /><path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" /><circle cx="17.5" cy="8.8" r="2.5" /><path d="M16.6 14.3a6 6 0 0 1 4.6 5.7" /></symbol>
      <symbol id="i-store" viewBox="0 0 24 24"><path d="M4 10v10h16V10M2.5 10 4.2 4.5h15.6L21.5 10M9.5 20v-6h5v6" /></symbol>
      <symbol id="i-mic" viewBox="0 0 24 24"><rect x="9.4" y="3" width="5.2" height="10.5" rx="2.6" /><path d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v4M9.5 21h5" /></symbol>
      <symbol id="i-utensils" viewBox="0 0 24 24"><path d="M6 3v6.5a2 2 0 0 0 4 0V3M8 9.5V21M17.2 3c-1.6 1-2.7 3-2.7 5.6 0 2 1.2 3.4 2.7 3.4s2.7-1.4 2.7-3.4c0-2.6-1.1-4.6-2.7-5.6zM17.2 12v9" /></symbol>
      <symbol id="i-panel" viewBox="0 0 24 24"><path d="M3 5h13v9H8l-5 3.5V14H3z" /><path d="M19 9.5h2V19l-3-2.5h-6.5" /></symbol>
      <symbol id="i-factory" viewBox="0 0 24 24"><path d="M3 20.5h18V10l-5.5 3.5V10L10 13.5V6.5H3z" /><path d="M6.5 17h2M12 17h2M17 17h1.5" /></symbol>
      <symbol id="i-play" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><path d="M10.3 8.6 16 12l-5.7 3.4z" /></symbol>
      <symbol id="i-rocket" viewBox="0 0 24 24"><path d="M12 2.8c3.4 2.2 5.3 5.6 5.3 9.5L12 17.6 6.7 12.3c0-3.9 1.9-7.3 5.3-9.5z" /><circle cx="12" cy="10" r="1.9" /><path d="M9.2 16.3 6.6 21l4.1-1.9M14.8 16.3 17.4 21l-4.1-1.9" /></symbol>
      <symbol id="i-chart" viewBox="0 0 24 24"><path d="M4 3.5v17h16" /><path d="M7.5 15.5 11 11l3.2 2.6L20 7.5" /></symbol>
      <symbol id="i-award" viewBox="0 0 24 24"><circle cx="12" cy="8.5" r="5" /><path d="M8.8 13 7.5 21l4.5-2.4L16.5 21l-1.3-8" /></symbol>
      <symbol id="i-ticket" viewBox="0 0 24 24"><path d="M3 7.5h18v3.2a1.6 1.6 0 0 0 0 3.2v3.6H3v-3.6a1.6 1.6 0 0 0 0-3.2z" /><path d="M14 8.6v1.6M14 13.2v1.6M14 17.8v-1.6" /></symbol>
      <symbol id="i-star" viewBox="0 0 24 24"><path d="m12 3.4 2.7 5.5 6 .9-4.3 4.2 1 6-5.4-2.8-5.4 2.8 1-6L3.3 9.8l6-.9z" /></symbol>
      <symbol id="i-plane" viewBox="0 0 24 24"><path d="M21 12.2 3 18.5l3.8-6.3L3 5.9z" /><path d="M6.8 12.2h8" /></symbol>
      <symbol id="i-car" viewBox="0 0 24 24"><path d="M4 16.5h16v-4l-2-5H6l-2 5z" /><path d="M4 12.5h16" /><circle cx="7.5" cy="18.5" r="1.6" /><circle cx="16.5" cy="18.5" r="1.6" /></symbol>
      <symbol id="i-bed" viewBox="0 0 24 24"><path d="M3 19V8M3 13.5h18V19M21 19v-3" /><path d="M6.5 10h3.2a2 2 0 0 1 2 2v1.5H6.5z" /></symbol>
      <symbol id="i-mail" viewBox="0 0 24 24"><rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="m3.8 7 8.2 6 8.2-6" /></symbol>
      <symbol id="i-arrow-right" viewBox="0 0 24 24"><path d="M4.5 12h14M13 6.5l5.5 5.5-5.5 5.5" /></symbol>
      <symbol id="i-arrow-down" viewBox="0 0 24 24"><path d="M12 4.5v14M6.5 13l5.5 5.5L17.5 13" /></symbol>
      <symbol id="i-external" viewBox="0 0 24 24"><path d="M14 3.5h6.5V10M20.5 3.5 11 13" /><path d="M18 14v5.5a1.8 1.8 0 0 1-1.8 1.8H4.8A1.8 1.8 0 0 1 3 19.5V8.2a1.8 1.8 0 0 1 1.8-1.8H10" /></symbol>
      <symbol id="i-check" viewBox="0 0 24 24"><path d="m4.5 12.5 5 5 10-11" /></symbol>
      <symbol id="i-bulb" viewBox="0 0 24 24"><path d="M9.5 18.5h5M10.2 21.5h3.6" /><path d="M12 2.8a6.2 6.2 0 0 0-3.7 11.2c.7.5 1.2 1.3 1.2 2.2v.3h5v-.3c0-.9.5-1.7 1.2-2.2A6.2 6.2 0 0 0 12 2.8z" /></symbol>
      <symbol id="i-briefcase" viewBox="0 0 24 24"><rect x="2.5" y="7" width="19" height="13" rx="2" /><path d="M8.5 7V5.2a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2V7M2.5 12.5h19" /></symbol>
      <symbol id="i-user" viewBox="0 0 24 24"><circle cx="12" cy="7.5" r="3.4" /><path d="M5.2 20.5a6.8 6.8 0 0 1 13.6 0" /></symbol>
      <symbol id="i-bolt" viewBox="0 0 24 24"><path d="M13.2 2.5 5 13.5h5.6L10.8 21.5 19 10.5h-5.6z" /></symbol>
      <symbol id="i-wrench" viewBox="0 0 24 24"><path d="M20.2 5.2a4.6 4.6 0 0 1-6.1 6.1L5.4 20 4 18.6l8.7-8.7a4.6 4.6 0 0 1 6.1-6.1l-3 3 2 2z" /></symbol>
      <symbol id="i-home" viewBox="0 0 24 24"><path d="M3.5 10.3 12 3.8l8.5 6.5V20.5h-5.8v-5.4H9.3v5.4H3.5z" /></symbol>
      <symbol id="i-cap" viewBox="0 0 24 24"><path d="M2.5 9 12 5l9.5 4-9.5 4z" /><path d="M6.8 11v4.4c0 1.4 2.3 2.5 5.2 2.5s5.2-1.1 5.2-2.5V11M21.5 9.2V15" /></symbol>
      <symbol id="i-clock" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5.4l3.4 2" /></symbol>
      <symbol id="i-shield" viewBox="0 0 24 24"><path d="M12 2.8 4.5 5.8v6c0 4.2 3 8 7.5 9.4 4.5-1.4 7.5-5.2 7.5-9.4v-6z" /><path d="m8.8 12 2.3 2.3 4.1-4.5" /></symbol>
      <symbol id="i-edit" viewBox="0 0 24 24"><path d="M16.5 3.8 20.2 7.5 8.7 19H5v-3.7z" /><path d="M14 6.3 17.7 10M4 22h16" /></symbol>
    </defs>
  </svg>
);

/* ---------- brand mark ---------- */
const Mark = ({ onClick }) => (
  <button type="button" className="mark" onClick={onClick} aria-label="Back to top">
    <svg className="mark-glyph" viewBox="0 0 100 100" aria-hidden="true">
      <path
        d="M50 4 61 28 85 15 76 41 100 50 76 59 85 85 61 72 50 96 39 72 15 85 24 59 0 50 24 41 15 15 39 28Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinejoin="round"
      />
    </svg>
    <span className="mark-txt">
      <b>Biz Connect</b>
      <span>Chicago</span>
    </span>
  </button>
);

/* ---------- image with graceful "photograph to come" fallback ---------- */
const Shot = ({ file, alt, className, icon, label, caption }) => (
  <figure className={className}>
    <img
      src={`${IMG_BASE}/${file}`}
      alt={alt}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.hidden = true;
      }}
    />
    <div className="pending">
      <Ic name={icon} />
      {label && <b>{label}</b>}
      {label && <span>Photograph to come</span>}
    </div>
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

/* ---------- speaker avatar ----------
   Initials on a violet disc, with the photograph laid over them. No file, no
   problem: the <img> hides itself and the initials show through, so a missing
   portrait reads as deliberate. Decorative — every use sits beside the name in
   text, so announcing it again would just be noise. */
const Avatar = ({ name, className }) => (
  /* The tint goes on a custom property, not `background` directly: an inline
     background would beat the .sess.keynote override that lightens the disc
     against the violet gradient. */
  <span className={className} aria-hidden="true" style={{ "--av-tint": tintFor(name) }}>
    {initials(name)}
    <img
      src={speakerImg(name)}
      alt=""
      loading="lazy"
      onError={(e) => {
        e.currentTarget.hidden = true;
      }}
    />
  </span>
);

/* The platinum sponsors, in the order the committee supplied them.
 *
 * Every logo here is dark artwork on a light ground, which is why the tiles are
 * white on a dark band rather than the logos sitting straight on the plum: a
 * dark wordmark on #150c1d is an empty rectangle. It is also how a sponsor wall
 * conventionally reads, so the tile is not a workaround.
 *
 * The domain is printed under each logo rather than only living in the href.
 * A sponsor is paying for attribution, and a logo a reader cannot act on is
 * worth less than one they can type into a phone.
 *
 * Each URL was opened and confirmed before being listed — ezzymanufacturing.com
 * redirects to ezzymfg.com, which is the form stored, and Zee Signs is at
 * zeesignschicago.com rather than the zeesigns.com their wordmark implies. */
const PLATINUM = [
  { name: "Ezzy Manufacturing", file: "sponsor-ezzy-manufacturing.png", url: "https://ezzymfg.com/" },
  { name: "HarborTechAI", file: "sponsor-harbortechai.jpg", url: "https://harbortechai.com/" },
  { name: "Kettle & Oak", file: "sponsor-kettle-oak.jpg", url: "https://kettleandoak.com/" },
  { name: "Zee Signs", file: "sponsor-zee-signs.webp", url: "https://zeesignschicago.com/" },
];

/* Printed under the logo, so it has to read as something a person could type:
   no scheme, no www, no trailing slash. */
const prettyHost = (url) => new URL(url).host.replace(/^www\./, "");

/* ---------- the exhibitor floor ----------

   Tints rotate by position in the array rather than hashing the name. The
   speaker grid hashes because two speakers share initials and a hash can land
   them on the same colour; here the monograms are distinct enough that a steady
   rotation reads better than a scatter, and it keeps the grid from clumping
   three of one colour together. */
const exhibitorTint = (i) => AVATAR_TINTS[i % AVATAR_TINTS.length];

/* Two slot shapes, picked from whether a logo file exists — which the import
   script resolves against disk, not at runtime.

   A square is right for a monogram and wrong for a logo: most company logos are
   wordmarks, and a wordmark squeezed into 52px of square is an unreadable grey
   smear. BluPencil Consulting and SBS America are the proof — legible at 96×52,
   illegible at 52×52. The monogram stays underneath either way, so a 404 shows
   letters rather than an empty box. */
const ExhibitorMark = ({ name, logo, logoDark, index }) => (
  <span
    className={`exh-mark${logo ? " wide" : ""}${logo && logoDark ? " on-dark" : ""}`}
    aria-hidden="true"
    style={{ "--av-tint": exhibitorTint(index) }}
  >
    {companyMark(name)}
    {logo && (
      <img
        src={`${IMG_BASE}/${logo}`}
        alt=""
        loading="lazy"
        onError={(e) => {
          e.currentTarget.hidden = true;
        }}
      />
    )}
  </span>
);

function ExhibitorFloor() {
  /* null means "all". Storing the pavilion name rather than an index keeps the
     filter correct when the sheet is re-imported and the order changes. */
  const [pavilion, setPavilion] = useState(null);
  const shown = pavilion ? EXHIBITORS.filter((e) => e.pavilion === pavilion) : EXHIBITORS;

  return (
    <section className="sec" id="exhibitors">
      <div className="wrap">
        <div className="sec-head rv">
          <p className="eyebrow">
            <Ic name="store" />
            Exhibitors
          </p>
          <h2>Who is on the floor</h2>
          <p>
            {EXHIBITORS.length} businesses confirmed so far, across eight industry pavilions.
            More are being added as the committee confirms them &mdash; pick a pavilion to see
            who is in it.
          </p>
        </div>

        {/* Buttons, not links: this filters in place and changes no URL. A
            reader using a screen reader gets the count read out as part of the
            label, which is the whole point of the section. */}
        <div className="exh-filter rv" role="group" aria-label="Filter exhibitors by pavilion">
          <button
            type="button"
            className={`exh-chip${pavilion === null ? " on" : ""}`}
            aria-pressed={pavilion === null}
            onClick={() => setPavilion(null)}
          >
            All pavilions <span className="exh-n">{EXHIBITORS.length}</span>
          </button>
          {PAVILION_COUNTS.map(([name, n]) => (
            <button
              key={name}
              type="button"
              className={`exh-chip${pavilion === name ? " on" : ""}`}
              aria-pressed={pavilion === name}
              onClick={() => setPavilion(pavilion === name ? null : name)}
            >
              {name} <span className="exh-n">{n}</span>
            </button>
          ))}
        </div>

        {/* `rv` belongs on this wrapper and NOT on the cards. The reveal
            observer runs once on mount and `.rv` starts at opacity 0, so a card
            rendered later by a filter change would never be observed and would
            stay invisible. Containers are stable; their children are not. */}
        <div className="exh-grid rv">
          {shown.map((e) => (
            <article className="exh-card" key={e.name}>
              {/* The name carries the link; the mark repeats it for the mouse
                  but is aria-hidden with tabindex -1, so neither the tab order
                  nor a screen reader meets the same destination twice.
                  rel="noopener" is not optional with target="_blank" — without
                  it the opened page gets a handle on this one via
                  window.opener. */}
              {e.url ? (
                <a
                  className="exh-mark-link"
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <ExhibitorMark
                    name={e.name}
                    logo={e.logo}
                    logoDark={e.logoDark}
                    index={EXHIBITORS.indexOf(e)}
                  />
                </a>
              ) : (
                <ExhibitorMark
                    name={e.name}
                    logo={e.logo}
                    logoDark={e.logoDark}
                    index={EXHIBITORS.indexOf(e)}
                  />
              )}
              <div className="exh-body">
                <h4>
                  {e.url ? (
                    <a
                      className="exh-link"
                      href={e.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {e.name}
                      <Ic name="external" />
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  ) : (
                    e.name
                  )}
                </h4>
                <p className="exh-pav">{e.pavilion}</p>
                {e.blurb && <p className="exh-blurb">{e.blurb}</p>}
              </div>
            </article>
          ))}
        </div>

        <p className="exh-foot" aria-live="polite">
          {pavilion
            ? `Showing ${shown.length} of ${EXHIBITORS.length} exhibitors in ${pavilion}.`
            : `Showing all ${EXHIBITORS.length} confirmed exhibitors.`}
        </p>
      </div>
    </section>
  );
}

/* ---------- one registration section, one audience ----------
   Both sections are identical in structure, so they share this. `audience`
   keys into REGISTRATION above — the only thing that differs per section is
   which JotForm loads.

   The iframe is loading="lazy" (see JotFormEmbed), so the second form on the
   page does not fetch until the reader scrolls near it. That is what makes two
   embeds on one page acceptable.

   Two embeds also require JotFormEmbed to size each iframe itself rather than
   call window.jotformEmbedHandler, which supports only one form per page — see
   the comment in that component before reintroducing it. */
const RegisterSection = ({
  id,
  eyebrow,
  heading,
  lede,
  audience,
  ctaLabel,
  extraNote,
  pendingNote,
}) => {
  const { id: formId, minutes } = REGISTRATION[audience];
  const url = formId ? jotformUrl(formId) : null;

  return (
    <section className="sec" id={id}>
      <div className="wrap">
        <div className="sec-head rv">
          <p className="eyebrow">
            <Ic name="edit" />
            {eyebrow}
          </p>
          <h2>{heading}</h2>
          <p>{lede}</p>
        </div>
        <div className="form-shell rv">
          {url ? (
            <>
              <div className="form-note">
                <span>
                  <Ic name="clock" />
                  About {minutes} minutes
                </span>
                <span>
                  <Ic name="mail" />
                  Confirmation sent by email
                </span>
                {extraNote && (
                  <span>
                    <Ic name="ticket" />
                    {extraNote}
                  </span>
                )}
              </div>
              <div className="form-frame">
                <JotFormEmbed
                  url={url}
                  formId={formId}
                  title={`Biz Connect Chicago — ${heading}`}
                  scrollToTopOnLoad={false}
                  minHeight={900}
                />
              </div>
              <p className="form-fallback">
                <span>Form not loading? It opens fine in its own tab.</span>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {ctaLabel}
                  <Ic name="external" />
                </a>
              </p>
            </>
          ) : (
            /* No form yet. Say so and route to the committee — better an
               honest inbox than the wrong form silently collecting the wrong
               registrations. Reuses .form-note / .form-fallback so this needs
               no extra CSS. */
            <>
              <div className="form-note">
                <span>
                  <Ic name="clock" />
                  Form opening shortly
                </span>
                {extraNote && (
                  <span>
                    <Ic name="ticket" />
                    {extraNote}
                  </span>
                )}
              </div>
              <p className="form-fallback">
                <span>{pendingNote}</span>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                    `Biz Connect Chicago — ${heading}`
                  )}`}
                >
                  Email the committee
                  <Ic name="mail" />
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- live registration figures ----------
   Aggregates only — neither source holds a name or contact detail. Two sources,
   tried in order:
     1. /api/bizconnect-stats — live, when the Static Web App has
        JOTFORM_API_KEY. Answers 503 when it does not.
     2. /biz-connect/stats.json — a snapshot the deploy workflow bakes in at
        build time (scripts/fetch-bizconnect-stats.cjs) from the JotForm export
        committed at data/biz-connect-attendees.csv; it updates when a new
        export is committed (see data/README.md).
   Any failure of both (`npm start`, where /api does not exist and the dev
   server answers with index.html; no key anywhere; JotForm down) leaves this
   null and the page renders exactly as it did before. Not RTK Query:
   businessApi's baseUrl is the separate backend, and these are same-origin. */
const STATS_SOURCES = ["/api/bizconnect-stats", "/biz-connect/stats.json"];

async function loadAttendeeStats(signal) {
  for (const url of STATS_SOURCES) {
    try {
      const r = await fetch(url, { signal, headers: { Accept: "application/json" } });
      if (!r.ok) continue;
      const j = await r.json();
      if (j && Number.isFinite(j.attendees) && j.attendees > 0) return j;
    } catch (e) {
      if (e.name === "AbortError") return null;
    }
  }
  return null;
}

// Switched off while the numbers are low. Set to true (and remove `if: false`
// from the "Snapshot Biz Connect" step in the deploy workflow) to bring the
// hero count and the "In the room so far" panel back.
const SHOW_ATTENDEE_STATS = false;

function useAttendeeStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!SHOW_ATTENDEE_STATS) return undefined;
    const ctl = new AbortController();
    loadAttendeeStats(ctl.signal).then((j) => {
      if (j && !ctl.signal.aborted) setStats(j);
    });
    return () => ctl.abort();
  }, []);
  return stats;
}

const plural = (n, one, many) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

const BarList = ({ title, rows }) => {
  if (!rows || !rows.length) return null;
  // "Other" is the folded remainder and can out-count the biggest named group,
  // so scale to the real maximum rather than assuming rows[0].
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="reg-block">
      <h4>{title}</h4>
      <ul className="reg-bars">
        {rows.map(({ label, count }) => (
          <li key={label} className={label === "Other" ? "reg-other" : undefined}>
            <span className="reg-l">{label}</span>
            <span className="reg-track" aria-hidden="true">
              <i style={{ width: `${Math.max(4, (count / max) * 100)}%` }} />
            </span>
            <span className="reg-n">{count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AttendeeProfile = ({ stats }) => {
  const where = [
    stats.cities?.count > 1 && plural(stats.cities.count, "city", "cities"),
    stats.states > 1 && plural(stats.states, "state", "states"),
    stats.countries > 1 && plural(stats.countries, "country", "countries"),
  ].filter(Boolean);
  return (
    <div className="card wide reg-panel" aria-label="Who has registered so far">
      <p className="eyebrow">In the room so far</p>
      <h3>
        {plural(stats.attendees, "person", "people")} registered
        {where.length ? <small> &middot; {where.join(" · ")}</small> : null}
      </h3>
      <div className="reg-cols">
        <BarList title="What they do" rows={stats.roles} />
        <BarList title="Industries" rows={stats.industries} />
      </div>
      {stats.cities?.top?.length ? (
        <div className="reg-block">
          <h4>Coming from</h4>
          <ul className="reg-chips">
            {stats.cities.top.map(({ label, count }) => (
              <li key={label}>
                {label} <b>{count}</b>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default function BizConnectShowcase() {
  const [navOpen, setNavOpen] = useState(false);
  const attendeeStats = useAttendeeStats();
  // Registered goes second: exhibitors lead because an expo is judged on the
  // floor. Absent until the endpoint answers, so the hero never shows a 0.
  const glance = attendeeStats
    ? [
        GLANCE[0],
        ["user", attendeeStats.attendees.toLocaleString("en-US"), "Registered", "Attendees signed up so far"],
        ...GLANCE.slice(1),
      ]
    : GLANCE;
  const canvasRef = useRef(null);
  const rootRef = useRef(null);

  /* ---------- in-page navigation ----------
     Deliberately does not touch location.hash: App.js scrolls to top on every
     router location change, which would defeat the jump. */
  const jumpTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
    window.history.replaceState(null, "", `#${id}`);
    setNavOpen(false);
  }, []);

  const onJump = useCallback(
    (id) => (e) => {
      e.preventDefault();
      jumpTo(id);
    },
    [jumpTo]
  );

  /* ---------- honour a deep link on first paint ---------- */
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, []);

  /* The countdown that used to live here — a 1s interval driving four state
     cells — was removed with the clock itself on 17 Oct. GLANCE replaced it. */

  /* ---------- khatam (eight-point star) tessellation behind the hero ---------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = prefersReducedMotion();
    const TILE = 132;
    const DRIFT = 5;
    const COLOR_A = "rgba(227,190,99,0.30)";
    const COLOR_B = "rgba(194,153,238,0.20)";
    let w = 0;
    let h = 0;
    let raf = null;

    const starPath = (cx, cy, R) => {
      const r = R * 0.4142;
      ctx.beginPath();
      for (let i = 0; i < 16; i++) {
        const ang = (Math.PI / 8) * i - Math.PI / 2;
        const rad = i % 2 === 0 ? R : r;
        const x = cx + Math.cos(ang) * rad;
        const y = cy + Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (off) => {
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 1;
      const ox = -TILE + (off % TILE);
      const oy = -TILE + ((off * 0.55) % TILE);
      const cols = Math.ceil(w / TILE) + 2;
      const rows = Math.ceil(h / TILE) + 2;
      for (let cx = 0; cx < cols; cx++) {
        for (let cy = 0; cy < rows; cy++) {
          const x = ox + cx * TILE + TILE / 2;
          const y = oy + cy * TILE + TILE / 2;
          const odd = (cx + cy) % 2 === 0;
          ctx.strokeStyle = odd ? COLOR_A : COLOR_B;
          starPath(x, y, TILE * 0.44);
          ctx.stroke();
          if (odd) {
            ctx.beginPath();
            ctx.rect(x - TILE * 0.17, y - TILE * 0.17, TILE * 0.34, TILE * 0.34);
            ctx.stroke();
          }
        }
      }
    };

    resize();
    if (reduce) {
      draw(0);
    } else {
      let start = null;
      const loop = (ts) => {
        if (start === null) start = ts;
        draw(((ts - start) / 1000) * DRIFT);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    const onResize = () => {
      resize();
      if (reduce) draw(0);
    };
    window.addEventListener("resize", onResize);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  /* ---------- scroll reveal ---------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll(".rv"));
    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("in");
            io.unobserve(en.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );
    items.forEach((el, i) => {
      el.style.transitionDelay = `${Math.min(i % 3, 2) * 80}ms`;
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <div className="bcs" ref={rootRef}>
      <SEO
        title="Biz Connect Chicago"
        // No dates here, deliberately. A shared link's preview text is cached
        // by the scraper and outlives any edit, so a date in this string keeps
        // circulating in chats long after it changes. The venue does not move;
        // the dates might. They live on the page and in the card image, both of
        // which a reader sees fresh. Keep in step with api/og/index.js.
        description="Where Mumineen businesses meet, grow & thrive. Inspiration, innovation & opportunity at Compass Arena, Willowbrook, Illinois."
        // ?v=4 cache-buster — see the note in api/og/index.js OG_OVERRIDES.
        // Keep the version in step with that file. The card itself is built
        // from BizConnectShowcase.og-card.html, beside this file.
        image="https://tr.chicagojamaat.org/biz-connect/og.jpg?v=4"
        // "website", not "event" — event is not a valid OGP type and makes
        // Facebook/WhatsApp drop the preview image. Matches api/og/index.js.
        type="website"
        imageWidth={1200}
        imageHeight={630}
        imageType="image/jpeg"
        imageAlt="Biz Connect Chicago — Compass Arena, Willowbrook, Illinois"
      />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=Marcellus&display=swap"
        />
      </Helmet>

      <Sprite />

      {/* ---------- in-page section nav ---------- */}
      <nav className="nav" aria-label="Event sections">
        <div className="wrap nav-in">
          <Mark onClick={() => jumpTo("top")} />
          <button
            className="nav-toggle"
            id="navToggle"
            type="button"
            aria-expanded={navOpen}
            aria-controls="navLinks"
            onClick={() => setNavOpen((v) => !v)}
          >
            {navOpen ? "Close" : "Menu"}
          </button>
          <div className={`nav-links${navOpen ? " open" : ""}`} id="navLinks">
            {NAV_SECTIONS.map(([id, label]) => (
              <a key={id} href={`#${id}`} onClick={onJump(id)}>
                {label}
              </a>
            ))}
            {/* Generic "Register" lands on #passes ("Choose how you take
                part"), NOT on a form. Sending it straight to the attendee form
                made it identical to the attendee CTA and hid the exhibitor
                path from anyone who started here. Only audience-specific CTAs
                jump to a form. */}
            <a className="btn btn-primary" href="#passes" onClick={onJump("passes")}>
              Register Now
            </a>
          </div>
          <a className="btn btn-primary" href="#passes" onClick={onJump("passes")}>
            Register Now
          </a>
        </div>
      </nav>

      {/* ---------- hero ---------- */}
      <header className="hero" id="top">
        <canvas className="khatam" ref={canvasRef} aria-hidden="true" />
        <div className="wrap hero-in">
          <div>
            <p className="eyebrow">Anjuman-e-Saifee Chicago&nbsp; presents&nbsp; a Business Expo</p>
            <p className="hero-arabic">Where Mumineen businesses meet, grow &amp; thrive</p>
          </div>

          <h1>
            Biz Connect <em>Chicago</em>
          </h1>

          <p className="hero-lede">
            Entrepreneurs, business owners, manufacturers, retailers, innovators, startups and investors
            from across the community — two days to connect, collaborate and grow, under one roof in
            Chicagoland.
          </p>

          <div className="hero-meta">
            <div className="meta-item">
              <span className="k">
                <Ic name="calendar" />
                Dates
              </span>
              <span className="v">
                5–6 December 2026
                <small>26–27 Jamadul Ukhra 1448 &nbsp;·&nbsp; 8:30am – 3:30pm both days</small>
              </span>
            </div>
            <div className="meta-item">
              <span className="k">
                <Ic name="pin" />
                Venue
              </span>
              <span className="v">
                Compass Arena<small>Willowbrook, Illinois</small>
              </span>
            </div>
            <div className="meta-item">
              <span className="k">
                <Ic name="building" />
                Presented by
              </span>
              <span className="v">
                Anjuman-e-Saifee<small>Chicago</small>
              </span>
            </div>
          </div>

          <ul className="glance-grid hero-stats rv" aria-label="The event at a glance">
            {glance.map(([icon, figure, label, sub]) => (
              <li key={label}>
                <Ic name={icon} />
                <b>{figure}</b>
                <span className="g-l">{label}</span>
                <span className="g-s">{sub}</span>
              </li>
            ))}
          </ul>

          {/* The proof stack. Three reasons to come, above the buttons,
              because the names were the strongest thing on this page and they
              sat 55-60% down it: a reader decided whether to register long
              before meeting them. Numbers persuade nobody; "the former CEO of
              Wipro" and "two sitting Congressmen" do. */}
          <ul className="hero-proof rv">
            <li>
              <Ic name="factory" />
              <span>
                <strong>{EXHIBITOR_COUNT} exhibitors and more to come</strong>, across eight
                industry pavilions &mdash; with a dedicated focus on <strong>manufacturing</strong>.
              </span>
            </li>
            <li>
              <Ic name="mic" />
              <span>
                <strong>Opening keynote: Abidali Neemuchwala</strong> &mdash; Tech Investor and
                Ex-CEO Wipro.
              </span>
            </li>
            <li>
              <Ic name="star" />
              <span>
                <strong>Guests of honour:</strong> Congressmen <strong>Raja Krishnamoorthi</strong>{" "}
                and <strong>Jonathan L. Jackson</strong>.
              </span>
            </li>
          </ul>

          {/* Both audiences get a hero CTA. Exhibitors lead: theirs is the
              "most enquiries" pass card, and the page's paid inventory —
              booths and sponsorship — sits on that path. btn-gold is the one
              filled button here, so first position and the fill go together;
              giving attendees the outline keeps a single primary action.
              .hero-cta is flex-wrap, so three buttons wrap cleanly. */}
          <div className="hero-cta">
            <a
              className="btn btn-gold"
              href="#register-exhibitor"
              onClick={onJump("register-exhibitor")}
            >
              Exhibit with us
              <Ic name="store" />
            </a>
            <a
              className="btn btn-outline-gold"
              href="#register-attendee"
              onClick={onJump("register-attendee")}
            >
              Register to attend
              <Ic name="arrow-right" />
            </a>
            <a className="btn btn-outline-gold" href="#about" onClick={onJump("about")}>
              Read the vision
              <Ic name="arrow-down" />
            </a>
          </div>

          {/* The page had no urgency of any kind: no deadline, no "closes", no
              limited anything. Ticket prices were removed from this line, and the
              rest of the page, on 21 Oct at the committee's request; entry is now
              free, and the deadline is the only urgency left. Say "free" and
              "required" in the same breath — free on its own reads as walk-up,
              which is exactly what pre-registration is meant to prevent.
              TODO(committee): 3 December is a PLACEHOLDER — nobody has set this
              date. Confirm it or change it. If registration has no cut-off, drop
              the closing clause rather than inventing a date. */}
          <p className="hero-deadline rv">
            <Ic name="clock" />
            <span>
              Entry is <strong>free</strong>, but pre-registration is required &mdash; registration
              closes <strong>3 December</strong>.
            </span>
          </p>
        </div>
      </header>

      {/* ---------- platinum sponsors ----------
          Takes the slot under the hero that the keynote promo held until 4 Nov.
          A sponsor at the top tier is buying the most-seen position on the page
          and this is it: first thing after the fold, before the reader has
          decided anything.

          White tiles on the dark band because every logo supplied is dark
          artwork on a light ground — on #150c1d they would be empty rectangles.
          It is also how a sponsor wall conventionally reads, so the tile is a
          choice rather than a workaround. */}
      <section className="band" id="platinum">
        <div className="wrap platinum">
          <div className="platinum-head rv">
            <p className="eyebrow">
              <Ic name="award" />
              Platinum sponsors
            </p>
            <h2>Made possible by</h2>
            <p>
              Four businesses backing the weekend at the top tier. Visit them on the floor, or
              follow a logo through to their work.
            </p>
          </div>
          <ul className="platinum-grid rv">
            {PLATINUM.map((s) => (
              <li key={s.name}>
                <a href={s.url} target="_blank" rel="noopener noreferrer">
                  <span className="plat-tile">
                    <img src={`${IMG_BASE}/${s.file}`} alt={s.name} loading="lazy" />
                  </span>
                  <span className="plat-host">
                    {prettyHost(s.url)}
                    <Ic name="external" />
                    <span className="sr-only"> (opens in a new tab)</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>



      {/* ---------- vision ---------- */}
      <section className="sec" id="about">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">The Vision</p>
            <h2>Business runs in our veins</h2>
          </div>
          <div className="vision">
            <figure className="quote rv">
              <blockquote>
                “Business is a defining characteristic of the Dawoodi Bohra community. Business comes
                instinctively to Mumineen — it runs in their veins, just as the muhabbat of Awliya Allah AS
                does.”
              </blockquote>
              <figcaption>
                From the Mawaaiz Nooraniyah of Aqa Moula (TUS) on <em>Tijaarat Hunar</em>, delivered during
                Ashara Mubarak.
              </figcaption>
            </figure>
            <div className="vision-body rv">
              <p>
                Inspired by this guidance, and with the <strong>Dua Mubarak of Aqa Moula (TUS)</strong>,
                Anjuman-e-Saifee Chicago and Al-Tijaarat al-Raabehah Chicago present a premier two-day
                Business &amp; Innovation Showcase.
              </p>
              <p>
                Biz Connect Chicago brings together entrepreneurs, business owners, professionals,
                manufacturers, retailers, innovators, startups, investors and aspiring leaders — to{" "}
                <strong>connect, collaborate and grow</strong>.
              </p>
              <p>
                More than an exhibition, it is a platform to exchange ideas, discover opportunities, build
                lasting partnerships and strengthen the economic future of our community.
              </p>
              <p>
                Two days of keynotes, working sessions, a full exhibition floor and deliberate
                networking, hosted at the spectacular Compass Arena in Willowbrook, Illinois. A
                state-of-the-art venue, and the right setting for business that starts as conversation and
                ends as partnership.
              </p>
              <p>
                <strong>Registration is open.</strong> Complete your registration today and be part of a
                gathering where businesses connect, collaborate and thrive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- who attends ---------- */}
      <section className="sec alt" id="who">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">Who attends</p>
            <h2>Who you&rsquo;ll meet</h2>
            <p>
              Every seat has a purpose. These are the people in the room and what each of them came
              for &mdash; so you know who to look for, and who is looking for you.
            </p>
          </div>
          <div className="aud-grid rv">
            {AUDIENCE.map(([icon, role, motive]) => (
              <div className="aud" key={role}>
                <Ic name={icon} />
                <div>
                  <h4>{role}</h4>
                  <p>{motive}</p>
                </div>
              </div>
            ))}
          </div>
          {/* `rv` on this always-rendered wrapper, not on the panel: the panel
              mounts after the fetch, once the reveal observer has already run
              and would never see it. See the note on .exh-grid. */}
          <div className="rv">{attendeeStats && <AttendeeProfile stats={attendeeStats} />}</div>
        </div>
      </section>

      {/* ---------- what's in it for you ---------- */}
      <section className="sec">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">What&rsquo;s in it for you</p>
            <h2>Eight reasons to attend</h2>
            <p>
              People, ideas and opportunities &mdash; two days built so you leave with more of all
              three than you arrived with.
            </p>
          </div>
          <div className="aud-grid rv">
            {WHY_ATTEND.map(([icon, title, body]) => (
              <div className="aud" key={title}>
                <Ic name={icon} />
                <div>
                  <h4>{title}</h4>
                  <p>{body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Exhibitors are the paying audience and bizconnectchicago.org does
              not serve them at all, so their case stays on this page — full
              width under the attendee reasons rather than in a second column,
              which would have forced the eight above into half the space. */}
          <div className="card wide rv">
            <span className="card-ic">
              <Ic name="store" lg />
            </span>
            <p className="eyebrow">For exhibitors</p>
            <h3>Why exhibit</h3>
            <ul className="tick two">
              {WHY_EXHIBIT.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- exhibitors ----------
          Sits here, directly under "who attends" and above the registration
          choice, because the hero's lead proof point is the exhibitor floor and
          until now the page claimed it and never showed it. A reader deciding
          whether to come should meet the floor before being asked to pick a
          pass, not three screens after.

          The pavilion strip doubles as the filter. Fifty-three cards is past
          the point where a flat grid is browsable on a phone, and the counts
          are the thing the committee asked to highlight, so one control does
          both jobs rather than adding a filter bar under a stat row that says
          the same numbers. */}
      <ExhibitorFloor />

      {/* ---------- passes ---------- */}
      <section className="sec alt" id="passes">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">Registration</p>
            <h2>Choose how you take part</h2>
            <p>
              Attendees and exhibitors each have their own registration form — pick the one that matches
              how you are joining and we will follow up with the details for your pass. Booth and
              sponsorship rates are in the <a href="#pricing" onClick={onJump("pricing")}>pricing
              section</a>.
            </p>
          </div>
          <div className="passes">
            <div className="pass rv">
              <span className="card-ic">
                <Ic name="ticket" lg />
              </span>
              <h3>Attendee</h3>
              {/* The $5.00 entrance fee came out on 21 Oct at the committee's
                  request and the block returned the same day as "Free": a card
                  with a price slot on every sibling looks unpriced, not free, if
                  the slot is simply left empty. The small print carries the
                  condition, because free without it invites walk-ups.
                  The attendee JotForm still states a fee and asks for Zelle
                  payment — that has to change on JotForm itself. */}
              <div className="price">
                Free<small>Pre-registration required</small>
              </div>
              <ul className="tick">
                <li>Full two-day access to keynotes, panels and sessions</li>
                <li>Entry to the exhibition floor across both days</li>
                <li>One ticket covers both days — come to either, or both</li>
                {/* The Business Reception Darees inclusion was removed with
                    Friday 4 December — see DAYS. Do not restore it unless the
                    Darees is back on the programme. */}
                <li>Attendee badge and event materials</li>
                <li>Automatic, exclusive raffle ticket (prizes include big-screen TV, iPad, and more!)</li>
              </ul>
              <a
                className="btn btn-primary"
                href="#register-attendee"
                onClick={onJump("register-attendee")}
              >
                <Ic name="ticket" />
                Register as an attendee
              </a>
            </div>
            <div className="pass feature rv">
              <span className="card-ic">
                <Ic name="store" lg />
              </span>
              <p className="eyebrow">Most enquiries</p>
              <h3>Exhibitor</h3>
              {/* Keep in step with BOOTHS — this is the cheapest booth in it. */}
              <div className="price">
                From $350<small>Per booth · limited spaces</small>
              </div>
              <ul className="tick">
                <li>Booth space in the Business Expo &amp; Product Showcase</li>
                <li>Listing in the event directory and on this site</li>
                <li>Attendee access for your booth team</li>
                <li>A place in the Manufacturing Pavilion or live demonstrations, by sector</li>
                {/* "Introduction from the stage during the Exhibitors &
                    Industry Leaders session" was here. That session was on
                    Friday 4 December and went with it, and the published
                    agenda (see AGENDA) has no equivalent slot — so the bullet
                    was promising something that does not exist. The matching
                    clause in the booth FAQ was removed at the same time. */}
              </ul>
              <a
                className="btn btn-primary"
                href="#register-exhibitor"
                onClick={onJump("register-exhibitor")}
              >
                <Ic name="store" />
                Enquire about a booth
              </a>
            </div>
            <div className="pass rv">
              <span className="card-ic">
                <Ic name="star" lg />
              </span>
              <h3>Sponsor</h3>
              {/* Keep in step with SPONSORSHIP — Community is the entry tier. */}
              <div className="price">
                From $253<small>Five tiers · Platinum to Community</small>
              </div>
              <ul className="tick">
                <li>Brand presence across the venue and event collateral</li>
                <li>Speaking or session-hosting opportunities by tier</li>
                <li>Premium floor placement</li>
                <li>Recognition in the closing session</li>
              </ul>
              {/* Tiers are published now, so this leads to the table rather
                  than straight to the inbox — #pricing carries its own
                  "Ask about sponsorship" mailto for the next step. */}
              <a className="btn btn-ghost" href="#pricing" onClick={onJump("pricing")}>
                <Ic name="star" />
                See the packages
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- pricing ----------
          Directly below "Choose how you take part": the pass cards quote a
          "from" figure, and this answers the "from what?" immediately instead
          of making the reader carry the question past two forms.

          The cost is that both forms now sit a section lower than they used to.
          Committee's call — if the forms need to come back up, move this block
          after #register-exhibitor rather than splitting the two tables up.

          Both tables are wrapped in .table-scroll — the sponsorship one is six
          columns and cannot fit a phone. The wrapper is the scroll container,
          not the table, so the sticky first column has something to stick to.
          tabindex + role make an overflowing table keyboard-scrollable. */}
      <section className="sec" id="pricing">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">
              <Ic name="ticket" />
              Pricing
            </p>
            <h2>Booths &amp; sponsorship packages</h2>
            <p>
              Attending is <strong>free</strong> — pre-registration is required. The rates below are for
              exhibitors and sponsors only, as confirmed by the committee — sponsorship at Platinum and
              Gold includes booth space, so there is no need to pay for both.
            </p>
          </div>

          <div className="price-block rv">
            <h3>Exhibitor pricing</h3>
            <div
              className="table-scroll"
              tabIndex="0"
              role="group"
              aria-labelledby="booth-table-cap"
            >
              <table className="ptable">
                <caption id="booth-table-cap" className="sr-only">
                  Exhibitor booth types, details and prices
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Booth type</th>
                    <th scope="col">Details</th>
                    <th scope="col" className="num">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {BOOTHS.map((b) => (
                    <tr key={b.type}>
                      <th scope="row">{b.type}</th>
                      <td>{b.detail}</td>
                      <td className="num">{b.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="table-foot">
              <Ic name="store" />
              Booth spaces are limited. Submitting the exhibitor form registers your interest; it does
              not itself confirm a booth.
            </p>
          </div>

          <div className="price-block rv">
            <h3>Sponsorship packages &amp; benefits</h3>
            <div
              className="table-scroll"
              tabIndex="0"
              role="group"
              aria-labelledby="sponsor-table-cap"
            >
              <table className="ptable ptable-wide">
                <caption id="sponsor-table-cap" className="sr-only">
                  Sponsorship packages and the benefits included at each tier
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Package benefit</th>
                    {SPONSOR_TIERS.map((tier) => (
                      <th scope="col" key={tier}>
                        {tier}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SPONSORSHIP.map((row) => (
                    <tr key={row.benefit} className={row.head ? "row-price" : undefined}>
                      <th scope="row">{row.benefit}</th>
                      {row.values.map((value, i) => (
                        <td key={SPONSOR_TIERS[i]}>
                          {value === true ? (
                            <>
                              <Ic name="check" />
                              <span className="sr-only">Included</span>
                            </>
                          ) : value === null ? (
                            <>
                              <span aria-hidden="true">—</span>
                              <span className="sr-only">Not included</span>
                            </>
                          ) : (
                            value
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="table-foot">
              <Ic name="bulb" />
              Digital promotion &amp; attendee access includes a website advertisement, website logo
              placement, exhibitor-list advertisement, and access to the attendee list.
            </p>
          </div>

          {/* The "Book a booth" button that used to sit here is gone: the
              exhibitor form is now the very next section, and a button whose
              only job is to scroll one screen down is noise.

              The sponsorship mailto stays. Sponsors have no form to fall into —
              without this they would read the packages and have nothing to act
              on until the sponsor band much further down the page. */}
          <div className="price-cta rv">
            <a
              className="btn btn-primary"
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                "Biz Connect Chicago — Sponsorship pack request"
              )}`}
            >
              <Ic name="mail" />
              Ask about sponsorship
            </a>
          </div>
        </div>
      </section>

      {/* ---------- registration: attendees ----------
          Exhibitor first, against the usual instinct to lead with the bigger
          audience: the section immediately above is booth and sponsorship
          rates, so the booth form is the one the reader is mid-decision about.
          The attendee path does not depend on the tables and survives being
          second — and every attendee entry point (hero CTA, pass card, footer,
          #register-attendee) still lands directly on its own form.

          Two sections rather than one tabbed section, so each audience keeps a
          linkable anchor. Both are plain `sec` (not `alt`), same as #pricing
          above them: passes is `alt` and programme is `alt`, so pricing plus
          the two forms share one unshaded run between them and read as a single
          decide-then-register block. */}
      <RegisterSection
        id="register-exhibitor"
        eyebrow="Registration · Exhibitors"
        heading="Book a booth"
        lede="For businesses taking booth space in the Business Expo. Booth types and rates are in the pricing table above. This form registers your interest — the committee follows up to confirm booth space and payment."
        audience="exhibitor"
        ctaLabel="Open the exhibitor form"
        extraNote="Exhibitor passes include attendee access for your booth team"
      />

      {/* ---------- registration: attendees ---------- */}
      <RegisterSection
        id="register-attendee"
        eyebrow="Registration · Attendees"
        heading="Register to attend"
        lede="Two days of keynotes, panels and the exhibition floor. Entry is free, but everyone attending must pre-register. Your registration includes an exclusive raffle ticket (prizes include big-screen TV, iPad, and more!) If you want a booth as well, use the exhibitor form above instead."
        audience="attendee"
        ctaLabel="Open the attendee form"
        extraNote="Free entry · pre-registration required · attendees must be over 16"
      />

      {/* ---------- programme ---------- */}
      <section className="sec alt" id="programme">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">Programme</p>
            <h2>Two days of inspiration, innovation &amp; opportunity</h2>
            <p>
              Each day has its own job. Day one puts the businesses in front of the room, day two turns
              all of it into what comes next.
            </p>
          </div>
          <div className="days rv">
            {DAYS.map((day) => (
              <article className="day" key={day.no}>
                <div className="day-no">{day.no}</div>
                <div className="day-body">
                  <h3>
                    {day.title}
                    <span className="day-theme">{day.theme}</span>
                  </h3>
                  <p className="day-date">{day.date}</p>
                  <p className="day-focus">{day.focus}</p>
                  <ul className="day-list">
                    {day.items.map(([icon, label]) => (
                      <li key={label}>
                        <Ic name={icon} />
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
          {/* h3, not a styled <p>: the timetable is a named sub-part of this
              section, so it should sit under the h2 in the document outline
              rather than just look like a heading. */}
          <h3 className="agenda-head rv">Biz Connect Chicago 2026 Speaker Agenda</h3>
          {/* ONE grid, not two stacks. Both days run the same seven hours, so
              the hours live in a rail down the left and each row holds that
              hour on both days — the two keynotes line up, "what is on at 1pm"
              is a glance, and fourteen repeated time labels become seven.

              Two independent columns drifted instead: Saturday 10am is a
              one-line open slot while Sunday 10am carries four speakers, so by
              11am the keynotes sat ~150px apart.

              Below 880px there is no room for a rail plus two columns, so it
              collapses to one column grouped BY HOUR — the rail label becomes a
              row heading and each card names its own day. Grouping by day
              instead would need a different DOM order than the desktop grid. */}
          <div className="sched">
            <p className="sched-corner" aria-hidden="true" />
            {AGENDA.map((d) => (
              <p className="sess-dayhead" key={d.day}>
                {d.day}
                <small>{d.meta}</small>
              </p>
            ))}
            {AGENDA_HOURS.map((hour) => (
              <React.Fragment key={hour}>
                <p className="sched-hour">{hour}</p>
                {AGENDA.map((d) => {
                  const s = d.sessions.find((x) => x.time === hour);
                  if (!s) return <div className="sched-blank" key={d.day} aria-hidden="true" />;
                  return (
                    <article
                      className={`sess rv${s.keynote ? " keynote" : ""}${s.open ? " open" : ""}`}
                      key={d.day}
                    >
                      {/* Day on the card, hour in the rail — the pairing swaps
                          on mobile, where the hour heads the row instead. */}
                      <p className="sess-when">{d.day}</p>
                      {s.keynote && <p className="sess-time">Keynote</p>}
                      <h4>{s.open ? "Programme to be announced" : s.title}</h4>
                      {s.note && <p className="sess-note">{s.note}</p>}
                      {s.soon && (
                        <p className="sess-tba">
                          <span className="soon">Speaker to be announced</span>
                        </p>
                      )}
                      {s.tba && <p className="sess-tba">{s.tba}</p>}
                      {s.speakers && (
                        <div className="spk-row">
                          {s.speakers.map((name) => (
                            <span className="spk" key={name}>
                              <Avatar name={name} className="spk-av" />
                              <span className="spk-n">
                                {name}
                                {speakerTitle(name) && <small>{speakerTitle(name)}</small>}
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </article>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>


      {/* ---------- keynote promo ----------
          The committee's own social card for the Saturday keynote, used at
          size rather than shrunk into an agenda cell. It sits straight after
          the programme so it lands on a reader who has just seen the 11:00 am
          slot, and it is the one place on the page with a direct "register"
          CTA attached to a named draw.

          It spent 17 Oct - 4 Nov directly under the hero. The platinum sponsors
          band took that position on 4 Nov; this moved back to its original home
          rather than being deleted, so the artwork and the CTA both survive.

          The artwork carries his name and title in its own typography, so the
          copy beside it deliberately does NOT repeat them — it gives the slot,
          the session and the action instead. */}
      <section className="band" id="keynote">
        <div className="wrap keynote-promo">
          <figure className="keynote-art rv">
            <img
              src={`${IMG_BASE}/keynote-abidali-neemuchwala.jpg`}
              alt="Abidali Zainuddin Neemuchwala, Tech Investor and Ex-CEO Wipro — The Biz Connect, come hear him speak."
              loading="lazy"
              onError={(e) => {
                e.currentTarget.closest("figure").hidden = true;
              }}
            />
          </figure>
          <div className="rv">
            <p className="eyebrow">Saturday 11:00 am &middot; Opening keynote</p>
            <h2>CEO Stories</h2>
            <p>
              Prominent CEOs share their journeys and what they learned along the way &mdash; the
              weekend opens with the one session everybody in the room has a reason to be at.
            </p>
            <div className="band-cta">
              <a
                className="btn btn-gold"
                href="#register-attendee"
                onClick={onJump("register-attendee")}
              >
                Register to attend
                <Ic name="arrow-right" />
              </a>
              <a className="btn btn-outline-gold" href="#programme" onClick={onJump("programme")}>
                See the full agenda
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- speakers ----------
          The people. The timetable they appear in lives in #programme above,
          under the two day cards — so this section must NOT say "sessions are
          listed below", which is what it said while the agenda sat here. */}
      <section className="sec" id="speakers">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">Speakers</p>
            <h2>Who you will hear from</h2>
          </div>

          {/* Cards, not the bare 88px disc strip this replaced. The strip gave
              a name and a session and nowhere to put a title, so the one
              speaker with real credentials had them buried in an agenda card;
              it also ended 6/6/2, leaving the last row stranded. The keynote
              takes the full first row — he is the headline booking and was
              otherwise indistinguishable from the other thirteen. */}
          <div className="spk-grid rv">
            {SPEAKERS.map((s) => (
              <article className={`spk-card${s.keynote ? " key" : ""}`} key={s.name}>
                <Avatar name={s.name} className="spk-card-av" />
                <div className="spk-card-body">
                  {s.keynote && <p className="key-badge">Keynote</p>}
                  <h4>{s.name}</h4>
                  {s.title && <p className="spk-title">{s.title}</p>}
                  <p className="spk-session">{s.session}</p>
                  {/* Collapsed by default. Four of twenty speakers have a bio,
                      and printing them open made those four cards several times
                      taller than their neighbours — grid rows stretch to the
                      tallest card, so one long bio left a hole beside every
                      short card in its row. A summary line costs one line and
                      keeps the committee's text rather than discarding it. */}
                  {s.bio && (
                    <details className="spk-more">
                      <summary>Read bio</summary>
                      <p>{s.bio}</p>
                    </details>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- guests of honour ----------
          Cards like the speakers', but these are rectangular official
          portraits, so the frame is a rounded rectangle rather than a disc —
          a circular crop would cut the composition of a formal portrait, and
          the difference in shape also says "not a speaker" without a label. */}
      <section className="sec alt" id="guests">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">Guests of honour</p>
            <h2>Distinguished guests</h2>
            <p>
              Meet the leaders bringing public service, enterprise and community progress to Biz
              Connect Chicago.
            </p>
          </div>
          <div className="guest-grid rv">
            {GUESTS.map((g) => (
              <article className={`guest${g.img ? "" : " no-photo"}`} key={g.name}>
                <span className="guest-shot" style={{ "--av-tint": tintFor(g.name) }}>
                  {g.initials || initials(g.name)}
                  {g.img && (
                    <img
                      src={`${IMG_BASE}/${g.img}`}
                      alt={`${g.name}, ${g.role}`}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.hidden = true;
                      }}
                    />
                  )}
                </span>
                <div className="guest-body">
                  {g.honorific && <p className="guest-hon">{g.honorific}</p>}
                  <h4>{g.name}</h4>
                  <p className="guest-role">{g.role}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- venue ---------- */}
      <section className="sec" id="venue">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">The Venue</p>
            <h2>Compass Arena, Willowbrook</h2>
            <p>
              A state-of-the-art arena and dining destination in Chicagoland — open floor for the expo,
              rooms for sessions, and space to keep talking once the sessions end.
            </p>
          </div>
          <div className="venue">
            <div className="rv">
              <ul className="venue-facts">
                {VENUE_FACTS.map(([icon, label, value]) => (
                  <li key={label}>
                    <b>
                      <Ic name={icon} />
                      {label}
                    </b>
                    <span>{value}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="venue-gallery rv">
              <Shot
                file="venue-exterior.jpg"
                alt="The exterior of Compass Arena in Willowbrook, Illinois"
                className="shot primary"
                icon="building"
                label="Exterior"
                caption="625 Joliet Road · Willowbrook, IL"
              />
            </div>
          </div>
          <div className="venue-strip rv">
            <Shot
              file="venue-floor.jpg"
              alt="The main arena floor at Compass Arena, where the Business Expo will be held"
              className="shot small"
              icon="store"
              label="Expo floor"
              caption="The expo floor"
            />
            <Shot
              file="venue-dining.jpg"
              alt="The dining area at Compass Arena"
              className="shot small"
              icon="utensils"
              label="Dining"
              caption="Dining & reception"
            />
          </div>

          {/* The preferred hotel, out of the venue-facts list and into a block
              of its own now that there is one to name — see HOTEL. */}
          <div className="hotel rv">
            <figure className="hotel-shot">
              <img
                src={`${IMG_BASE}/${HOTEL.img}`}
                alt={HOTEL.alt}
                loading="lazy"
                onError={(e) => {
                  const shot = e.currentTarget.closest(".hotel-shot");
                  if (shot) shot.hidden = true;
                }}
              />
            </figure>
            <div className="hotel-body">
              <p className="eyebrow">
                <Ic name="bed" />
                Where to stay
              </p>
              <h3>{HOTEL.name}</h3>
              <p>{HOTEL.body}</p>
              <p className="hotel-links">
                <a
                  className="place-link"
                  href={mapsUrl(HOTEL.mapsQuery)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Directions &amp; phone
                  <Ic name="external" />
                </a>
                <a className="place-link" href={HOTEL.site} target="_blank" rel="noopener noreferrer">
                  Hotel website
                  <Ic name="external" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- sponsorship band ---------- */}
      <section className="band" id="sponsor">
        <div className="wrap band-in">
          <div className="rv">
            <p className="eyebrow">Sponsorship</p>
            <h2>Will you just be watching from the sidelines?</h2>
            <p>
              Five packages are open, from $253 at Community to $4,500 at Platinum — Platinum and Gold
              include booth space, a video advertisement and stage recognition. The full benefit
              comparison is in the pricing section.
            </p>
          </div>
          <div className="band-cta rv">
            <a
              className="btn btn-gold"
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                "Biz Connect Chicago — Sponsorship pack request"
              )}`}
            >
              <Ic name="mail" />
              Request the sponsorship pack
            </a>
            {/* Exhibitor, not attendee — this band's copy ends on "take a booth
                and put your product in the room". */}
            <a
              className="btn btn-outline-gold"
              href="#register-exhibitor"
              onClick={onJump("register-exhibitor")}
            >
              Take a booth
              <Ic name="arrow-right" />
            </a>
          </div>
        </div>
      </section>

      {/* ---------- faq ---------- */}
      <section className="sec" id="faq">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">Questions</p>
            <h2>Before you register</h2>
          </div>
          <div className="faq rv">
            {FAQS.map((f, i) => (
              <details key={f.q} open={i === 0}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- around chicago ---------- */}
      <section className="sec alt" id="chicago">
        <div className="wrap">
          <div className="sec-head rv">
            <p className="eyebrow">
              <Ic name="pin" /> Around Chicago
            </p>
            <h2>Make a weekend of it</h2>
            <p>
              Compass Arena is about 25 minutes from downtown Chicago. If you are travelling in for the
              event, these are worth the extra day.
            </p>
          </div>

          <div className="place-grid rv">
            {ATTRACTIONS.map((p) => (
              <article className="place" key={p.title}>
                <figure className="place-img">
                  <img
                    src={`${IMG_BASE}/${p.img}`}
                    alt={p.alt}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.hidden = true;
                    }}
                  />
                  <div className="pending">
                    <Ic name={p.icon} />
                  </div>
                </figure>
                <div className="place-body">
                  <p className="place-kind">{p.kind}</p>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                  <a className="place-link" href={p.href} target="_blank" rel="noopener noreferrer">
                    {p.cta}
                    <Ic name="external" />
                  </a>
                </div>
              </article>
            ))}
          </div>

          {/* Sits above the restaurant lists on purpose: nobody should read the
              dining section and conclude they have to leave the venue to eat. */}
          <div className="notice rv">
            <Ic name="utensils" />
            <span>
              <strong>Food is available on site.</strong> Food stalls and vendors will be at Compass
              Arena across both days serving halal food, so you do not need to leave the venue to eat.
              The restaurants below are for anyone who wants to make more of the trip.
            </span>
          </div>

          <div className="subhead rv">
            <h3>Halal dining near the venue</h3>
            <span>Willowbrook, Oak Brook and Naperville — drive times from Compass Arena</span>
          </div>

          <div className="place-grid rv">
            {DINING_NEAR_VENUE.map((d) => (
              <article className="place" key={d.title}>
                <div className="place-body">
                  <p className="place-kind">{d.kind}</p>
                  <h3>{d.title}</h3>
                  <p className="place-meta">
                    <Ic name="car" />
                    {d.meta}
                  </p>
                  <p>{d.body}</p>
                  <a
                    className="place-link"
                    href={mapsUrl(d.query)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Directions &amp; phone
                    <Ic name="external" />
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="subhead rv">
            <h3>Worth the drive into the city</h3>
            <span>25–40 minutes from the arena</span>
          </div>

          <div className="place-grid rv">
            {DINING_CITY.map((d) => (
              <article className="place" key={d.title}>
                <div className="place-body">
                  <p className="place-kind">{d.kind}</p>
                  <h3>{d.title}</h3>
                  <p>{d.body}</p>
                  <a className="place-link" href={d.href} target="_blank" rel="noopener noreferrer">
                    Details
                    <Ic name="external" />
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="notice rv">
            <Ic name="shield" />
            <span>
              Every restaurant above is drawn from{" "}
              <a
                href="https://www.zabihah.com/halal-restaurants/chicago-il-us"
                target="_blank"
                rel="noopener noreferrer"
              >
                Zabihah
              </a>
              , the community-verified halal directory, and none is listed as serving alcohol. Zabihah
              carries 200+ more across Chicago. <strong>Please still confirm directly before you go</strong>{" "}
              — ownership and certification change, and this list is a starting point rather than an
              endorsement. Drive times are approximate.
            </span>
          </div>
        </div>
      </section>

      {/* ---------- event contact band ---------- */}
      <section className="bcs-foot" id="contact">
        <div className="wrap">
          <div className="foot-grid">
            <div>
              <Mark onClick={() => jumpTo("top")} />
              <p className="foot-lede">
                An exclusive Business Expo presented by Anjuman-e-Saifee Chicago with Al-Tijaarat
                al-Raabehah Chicago, with the Dua Mubarak of Aqa Moula (TUS).
              </p>
            </div>
            <div>
              <h4>Event</h4>
              <button type="button" className="foot-link" onClick={onJump("about")}>About</button>
              <button type="button" className="foot-link" onClick={onJump("who")}>Who attends</button>
              <button type="button" className="foot-link" onClick={onJump("programme")}>Programme</button>
              <button type="button" className="foot-link" onClick={onJump("speakers")}>Speakers</button>
              <button type="button" className="foot-link" onClick={onJump("guests")}>Guests</button>
              <button type="button" className="foot-link" onClick={onJump("venue")}>Venue</button>
            </div>
            <div>
              <h4>Take part</h4>
              <button type="button" className="foot-link" onClick={onJump("register-attendee")}>Attend</button>
              <button type="button" className="foot-link" onClick={onJump("register-exhibitor")}>Exhibit</button>
              <button type="button" className="foot-link" onClick={onJump("sponsor")}>Sponsor</button>
              <a href={`mailto:${CONTACT_EMAIL}`}>Contact the committee</a>
            </div>
          </div>
          <div className="foot-bottom">
            <span>5–6 December 2026 · 26–27 Jamadul Ukhra 1448</span>
            <span>Compass Arena, 625 Joliet Road, Willowbrook, IL 60527</span>
            <span>© 2026 Anjuman-e-Saifee Chicago</span>
          </div>
        </div>
      </section>
    </div>
  );
}
